import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar, RefreshCw, Users, MapPin, Target, ChevronLeft, ChevronRight, Plus, ChevronDown, Search } from 'lucide-react';
import { useToast } from './ToastProvider';
import TicketCustomerField from './TicketCustomerField';
import CalendarTicketTitle from './CalendarTicketTitle';
import EventToTicketModal from './EventToTicketModal';
import apiService from '../services/apiService';
import TicketSidebar from './TicketSidebar';
import TicketDetail from './TicketDetail';
import CalendarEventDetail from './CalendarEventDetail';
import { CalenderMarkerHeaderSelect } from '../utils/calenderMarkerUtils';
import KalenderTicketKennung, { kennungPlatz, hatKennung } from './kalender/KalenderTicketKennung';
import { GebuchtMenuePunkt, useGebuchtUmschalten } from './kalender/Gebucht';
import { useI18n } from '../utils/i18n';
import Modal from './ui/Modal';
import { flagInvalidField } from '../utils/formValidation';
import { useConfirm } from './ui/ConfirmDialog';
import * as loadingDebug from '../utils/loadingDebug';
import useIsMobile from '../hooks/useIsMobile';
import { erstelleKalenderHelfer } from '../utils/kalenderHelfer';

// #308: stabile Rueckfallzelle -- ein Objektliteral im Render waere bei
// jedem Durchlauf ein neues.
const LEERE_ZELLE = { items: [], hasAbove: false, hasBelow: false };

const GlobalCalendarView = () => {
  // W6 (#16): TicketDetail bekommt auf schmalen Screens das kompakte Mobil-Layout
  const isMobileScreen = useIsMobile();
  const { pushToast } = useToast();
  // #604: „Gebucht“ aus dem Kontextmenü, danach den Tag neu laden
  const gebuchtUmschalten = useGebuchtUmschalten(async () => {
    await loadCalendarDataForDate(new Date(currentDate));
  });
  const { t, getLocale } = useI18n();
  const confirm = useConfirm();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [userEvents, setUserEvents] = useState({});
  const [userTickets, setUserTickets] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date()); // Can be changed with navigation
  
  // Current time marker state
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Drag & Drop State
  const [draggedTicket, setDraggedTicket] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null); // { userEmail, day }
  const [dragPreview, setDragPreview] = useState(null);
  const [showDragPreview, setShowDragPreview] = useState(false);
  
  // Planned ticket drag state
  const [draggedPlannedTicket, setDraggedPlannedTicket] = useState(null);
  const [dragStartPos, setDragStartPos] = useState(null);
  
  // Operation state to prevent concurrent drag operations
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [operationMessage, setOperationMessage] = useState('');
  
  // Resize State (improved like CalendarView)
  const [, setResizingTicket] = useState(null);
  const [, setResizeStartPos] = useState({ x: 0, y: 0 });
  const [, setResizeStartHeight] = useState(0);
  const [, setResizeHandle] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [showResizePreview, setShowResizePreview] = useState(false);
  const [justResized, setJustResized] = useState(false);
  const [resizeMode, setResizeMode] = useState(false);
  // Dynamic time window for global view (defaults 8-18)
  const [timeStartHour, setTimeStartHour] = useState(8);
  const [timeEndHour, setTimeEndHour] = useState(18);
  // #311: die gemeinsamen Kalender-Helfer aus utils/kalenderHelfer.js.
  // Arbeitszeitfenster und Sprache sind gebunden, damit die Aufrufstellen
  // unveraendert bleiben.
  const {
    getUserTimeZone, formatTime, formatTicketTime, getItemTimeRange,
    snapToGrid, extractTicketNumberFromEvent, getEventStatus, getTicketColor,
    isToday, getTimeSlots, getEventHeight, getTicketHeight,
    getEventPosition, getTicketPosition, getCurrentTimePosition, calculateItemPositions,
    tagesGrenzen, tagesEintraege, erstelleStundenFormat, ausserhalbFenster
  } = useMemo(
    () => erstelleKalenderHelfer({ timeStartHour, timeEndHour, locale: getLocale() }),
    [timeStartHour, timeEndHour, getLocale]
  );

  
  // Search state for global calendar (tickets + Graph)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]); // { type: 'event'|'ticket', id, start: Date }
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const [matchedEventIds, setMatchedEventIds] = useState([]);
  const [matchedTicketIds, setMatchedTicketIds] = useState([]);
  const [currentMatch, setCurrentMatch] = useState(null); // { type, id }
  
  // Mouse selection state for creating tickets directly from calendar
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionUserEmail, setSelectionUserEmail] = useState(null);
  const [selectionDay, setSelectionDay] = useState(null);
  const [selectionRect, setSelectionRect] = useState(null); // { topPercent, heightPercent }
  const [pendingPlannedTimeRange, setPendingPlannedTimeRange] = useState(null); // { startDateTime, endDateTime, userEmail }
  const selectionMouseMoveRef = useRef(null);
  const selectionMouseUpRef = useRef(null);
  const selectionContainerRectRef = useRef(null);
  const selectionStartYRef = useRef(0);
  
  // Ticket Detail Modal
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  // Event Detail Modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDetail, setShowEventDetail] = useState(false);
  // Drop-Entscheidungs-Modal (Verschieben vs. weiteren erstellen)
  const [dropChoiceModal, setDropChoiceModal] = useState({ visible: false, context: null });
  // Event-Konvertierung (Rechtsklick auf Graph-Termin)
  const [eventContextMenu, setEventContextMenu] = useState({ visible: false, x: 0, y: 0, event: null, userEmail: null });
  const [showEventConvertModal, setShowEventConvertModal] = useState(false);
  const [selectedEventForConvert, setSelectedEventForConvert] = useState(null);
  const [selectedUserForConvert, setSelectedUserForConvert] = useState(null);
  
  // Ticket Creation Form
  const [showCreateTicketForm, setShowCreateTicketForm] = useState(false);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'normal',
    assignedTo: '',
    category: '',
    tags: [],
    calenderMarker: null,
    customerId: null,
    contactId: null
  });
  
  const [currentUser, setCurrentUser] = useState(null);
  
  // User filter dropdown state
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  
  // View mode: 'day' | 'week' | 'weekend' (week incl. weekend)
  const [viewMode, setViewMode] = useState('week');
  const showWeekend = viewMode === 'weekend';

  // Single toggle for view mode (cycles like CalendarView's 14 Tage/Woche)
  const getNextViewMode = () => {
    switch (viewMode) {
      case 'day': return 'week';
      case 'week': return 'weekend';
      default: return 'day';
    }
  };

  const handleToggleViewMode = () => {
    const next = getNextViewMode();
    setViewMode(next);
    if (!highlightMode) {
      saveViewModeToCookie(next);
    }
  };

  const getViewModeLabel = (mode) => {
    switch (mode) {
      case 'day': return t('global.view.dayLabel', 'TAG');
      case 'week': return t('global.view.weekLabel', 'WOCHE');
      case 'weekend': return t('global.view.weekendLabel', 'WOCHE+WE');
      default: return t('global.view.weekLabel', 'WOCHE');
    }
  };

  const getViewModeTooltip = (mode) => {
    switch (mode) {
      case 'day': return t('global.onlyTodayTooltip', 'Nur aktueller Tag anzeigen');
      case 'week': return t('global.weekTooltip', 'Aktuelle Woche (Mo–Fr) anzeigen');
      case 'weekend': return t('global.weekendTooltip', 'Aktuelle Woche inkl. Wochenende (Mo–So) anzeigen');
      default: return '';
    }
  };

  // Perform search across entire calendar (tickets only) and navigate to first match
  const performSearch = useCallback(async (query) => {
    // Bump token so concurrent background refreshes can't override the result
    const token = ++activeNavLoadTokenRef.current;
    const q = (query || '').trim().toLowerCase();
    if (!q) {
      setMatchedEventIds([]);
      setMatchedTicketIds([]);
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setCurrentMatch(null);
      return;
    }

    const now = new Date();
    const rangeStart = new Date(now);
    rangeStart.setDate(rangeStart.getDate() - 365);
    rangeStart.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(now);
    rangeEnd.setDate(rangeEnd.getDate() + 365);
    rangeEnd.setHours(23, 59, 59, 999);

    try {
      const ticketsResp = await apiService.getPlannedTickets(rangeStart, rangeEnd);
      if (token !== activeNavLoadTokenRef.current) return;
      const allTickets = ticketsResp.tickets || [];

      const results = [];
      const tokens = q.split(/\s+/).filter(Boolean);
      const selectedEmails = new Set(Array.from(selectedUsers || []));
      const ticketsScope = selectedEmails.size > 0
        ? allTickets.filter(t => t.assignedTo && selectedEmails.has(t.assignedTo))
        : allTickets;
      ticketsScope.forEach(t => {
        const title = (t.title || '').toLowerCase();
        if (tokens.every(tok => title.includes(tok))) {
          const start = t.plannedStartDateTime ? new Date(t.plannedStartDateTime) : new Date();
          results.push({ type: 'ticket', id: t.id, start });
        }
      });

      results.sort((a, b) => a.start - b.start);
      if (token !== activeNavLoadTokenRef.current) return;
      setSearchResults(results);
      setMatchedEventIds([]);
      setMatchedTicketIds(results.filter(r => r.type === 'ticket').map(r => r.id));

      if (results.length > 0) {
        const nowMs = Date.now();
        let firstIndex = results.findIndex(r => r.start && r.start.getTime() >= nowMs);
        if (firstIndex === -1) firstIndex = results.length - 1; // all in past -> pick latest past
        const first = results[firstIndex];
        if (token !== activeNavLoadTokenRef.current) return;
        setCurrentSearchIndex(firstIndex);
        setCurrentMatch({ type: first.type, id: first.id });

        // Navigate to appropriate date based on view mode
        const targetDate = new Date(first.start);
        if (token !== activeNavLoadTokenRef.current) return;
        setCurrentDate(targetDate);
        await loadCalendarDataForDate(targetDate);
        // Do not auto-scroll
      } else {
        if (token !== activeNavLoadTokenRef.current) return;
        setCurrentSearchIndex(-1);
        setCurrentMatch(null);
      }
    } catch (e) {
      console.error('🌍 [GLOBAL CALENDAR] Error during global search:', e);
    }
  }, [selectedUsers]);

  const goToNextSearchResult = useCallback(async () => {
    if (!searchResults || searchResults.length === 0) return;
    if (currentSearchIndex >= searchResults.length - 1) return;
    const nextIndex = currentSearchIndex + 1;
    const next = searchResults[nextIndex];
    setCurrentSearchIndex(nextIndex);
    setCurrentMatch({ type: next.type, id: next.id });
    try {
      const targetDate = new Date(next.start);
      setCurrentDate(targetDate);
      await loadCalendarDataForDate(targetDate);
    } catch (_) {}
    // Do not auto-scroll
  }, [searchResults, currentSearchIndex]);

  const goToPrevSearchResult = useCallback(async () => {
    if (!searchResults || searchResults.length === 0) return;
    if (currentSearchIndex <= 0) return;
    const prevIndex = currentSearchIndex - 1;
    const prev = searchResults[prevIndex];
    setCurrentSearchIndex(prevIndex);
    setCurrentMatch({ type: prev.type, id: prev.id });
    try {
      const targetDate = new Date(prev.start);
      setCurrentDate(targetDate);
      await loadCalendarDataForDate(targetDate);
    } catch (_) {}
    // Do not auto-scroll
  }, [searchResults, currentSearchIndex]);

  const handleEventClick = (e, event) => {
    setSelectedEvent(event);
    setShowEventDetail(true);
  };
  // Projekt-Zeitplanung Filter
  const [projectPlanId, setProjectPlanId] = useState(null);
  const [, setProjectPlanName] = useState('');
  const [projectRelatedSubIds, setProjectRelatedSubIds] = useState(new Set());
  const [projectRelevantUserEmails, setProjectRelevantUserEmails] = useState(new Set());
  
  // Highlight-Modus (Ticket im Kalender finden)
  const [highlightMode, setHighlightMode] = useState(false);
  const [highlightTicketId, setHighlightTicketId] = useState(null);
  const [highlightUserEmail, setHighlightUserEmail] = useState(null);
  const [highlightStartIso, setHighlightStartIso] = useState(null);

  // Cookie helper functions
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  const saveSelectedUsersToCookie = (userEmails) => {
    const emailArray = Array.from(userEmails);
    setCookie('globalCalendarSelectedUsers', JSON.stringify(emailArray));
  };

  const loadSelectedUsersFromCookie = () => {
    const savedUsers = getCookie('globalCalendarSelectedUsers');
    if (savedUsers) {
      try {
        const emailArray = JSON.parse(savedUsers);
        return new Set(emailArray);
      } catch (error) {
        console.error('Error parsing saved user selection from cookie:', error);
        return null;
      }
    }
    return null;
  };
  
  // Persist view mode in cookie
  const saveViewModeToCookie = (mode) => {
    setCookie('globalCalendarViewMode', mode);
  };
  const loadViewModeFromCookie = () => {
    const saved = getCookie('globalCalendarViewMode');
    if (saved && (saved === 'day' || saved === 'week' || saved === 'weekend')) {
      return saved;
    }
    return null;
  };
  
  // Context Menu State
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    ticket: null
  });

  // Reference to store current resize values (like CalendarView)
  const currentResizeValues = useRef({ startDateTime: null, endDateTime: null });
  
  // References for event listeners cleanup (like CalendarView)
  const resizeMouseMoveRef = useRef(null);
  const resizeMouseUpRef = useRef(null);
  // Guard against stale async updates (navigation/search vs background refresh)
  const activeNavLoadTokenRef = useRef(0);
  
  // Reference to store the ticket removal function from TicketSidebar
  const removeTicketFromSidebar = useRef(null);

  // Callback to register the ticket functions from TicketSidebar
  const handleTicketPlannedCallback = (ticketFunctions) => {
    removeTicketFromSidebar.current = ticketFunctions;
  };

  // Cleanup function for resize event listeners (like CalendarView)
  const cleanupResizeListeners = () => {
    if (resizeMouseMoveRef.current) {
      document.removeEventListener('mousemove', resizeMouseMoveRef.current);
      resizeMouseMoveRef.current = null;
    }
    if (resizeMouseUpRef.current) {
      document.removeEventListener('mouseup', resizeMouseUpRef.current);
      resizeMouseUpRef.current = null;
    }
  };


  // Load data only on initial mount (navigation uses optimized refresh)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Initial data loading effect
  useEffect(() => {
    if (isInitialLoad) {
      const performInitialLoad = async () => {
        setLoading(true);
        setError(null);
        
        try {
          // Parse project plan params & highlight params
          try {
            const params = new URLSearchParams(location.search || '');
            const planId = params.get('projectPlanId');
            const planName = params.get('projectName') || '';
            if (planId) {
              setProjectPlanId(Number(planId));
              setProjectPlanName(planName);
            }

            // Highlight-Parameter auslesen (find in calendar)
            const hId = params.get('highlightTicketId');
            const hUser = params.get('highlightUser');
            const hStart = params.get('highlightStart');
            if (hId && hUser && hStart) {
              setHighlightMode(true);
              setHighlightTicketId(Number(hId));
              setHighlightUserEmail(String(hUser));
              setHighlightStartIso(String(hStart));
              // Erzwinge Wochenansicht im UI (nicht in Cookies persistieren)
              setViewMode('week');
              // Kalenderwoche anhand Start-Datum setzen
              try {
                const d = new Date(hStart);
                if (!isNaN(d)) {
                  const dow = d.getDay();
                  const diffToMonday = (dow === 0 ? -6 : 1 - dow);
                  const weekStart = new Date(d);
                  weekStart.setDate(d.getDate() + diffToMonday);
                  weekStart.setHours(0, 0, 0, 0);
                  setCurrentDate(weekStart);
                }
              } catch (_) {}
            }
          } catch (_) {}

          // Load all users first (Manager-taugliche Route, #185)
          const usersResponse = await apiService.getCalendarUsers();
          const allUsers = usersResponse.users || [];
          
          // Filter active users (remove strict licensing requirement for now)
          const activeUsers = allUsers.filter(user => {
            const isActive = user.isActive !== false; // Default to true if not specified
            const hasEmail = user.email && user.email.trim() !== '';
            return isActive && hasEmail;
          });
          
          setUsers(activeUsers);
          
          // Initialize selectedUsers
          if (selectedUsers.size === 0 && activeUsers.length > 0) {
            if (highlightMode && highlightUserEmail) {
              const availableUserEmails = new Set(activeUsers.map(user => user.email));
              if (availableUserEmails.has(highlightUserEmail)) {
                setSelectedUsers(new Set([highlightUserEmail]));
              } else {
                // Fallback: behalte Standardauswahl (alle Nutzer)
                const allUserEmails = new Set(activeUsers.map(user => user.email));
                setSelectedUsers(allUserEmails);
              }
            } else {
              const savedSelection = loadSelectedUsersFromCookie();
              if (savedSelection !== null) {
                if (savedSelection.size > 0) {
                  const availableUserEmails = new Set(activeUsers.map(user => user.email));
                  const validSavedUsers = new Set([...savedSelection].filter(email => availableUserEmails.has(email)));
                  setSelectedUsers(validSavedUsers);
                } else {
                  setSelectedUsers(new Set());
                }
              } else {
                const allUserEmails = new Set(activeUsers.map(user => user.email));
                setSelectedUsers(allUserEmails);
              }
            }
          }
          
          // Load saved view mode (Highlight-Modus überschreibt nicht die Cookies)
          const savedView = loadViewModeFromCookie();
          if (savedView && !highlightMode) {
            setViewMode(savedView);
          }

          // Determine current range by viewMode (Basis ggf. Highlight-Start)
          let dayStart, dayEnd;
          const baseDate = highlightMode && highlightStartIso ? new Date(highlightStartIso) : currentDate;
          if ((savedView || viewMode) === 'day') {
            dayStart = new Date(baseDate);
            dayStart.setHours(0, 0, 0, 0);
            dayEnd = new Date(baseDate);
            dayEnd.setHours(23, 59, 59, 999);
          } else {
            const today = new Date(baseDate);
            const day = today.getDay(); // 0=Sun,1=Mon...
            const diffToMonday = (day === 0 ? -6 : 1 - day);
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() + diffToMonday);
            weekStart.setHours(0, 0, 0, 0);
            dayStart = weekStart;
            dayEnd = new Date(weekStart);
            dayEnd.setDate(weekStart.getDate() + (showWeekend ? 6 : 4));
            dayEnd.setHours(23, 59, 59, 999);
          }
          
          // Load events and tickets for each user
          const userEventsData = {};
          const userTicketsData = {};
          
          // Prepare user emails for global events API
          const userEmails = activeUsers.map(user => user.email);
          
          // Load calendar events and planned tickets in parallel
          const [globalEventsResponse, allTicketsResponse] = await Promise.all([
            apiService.getGlobalEvents(userEmails, dayStart, dayEnd),
            apiService.getPlannedTickets(dayStart, dayEnd)
          ]);
          
          const allTicketsRaw = allTicketsResponse.tickets || [];
          let allTickets = allTicketsRaw;

          // Wenn Projekt-Planungsmodus aktiv: filtere geplante Tickets auf Projekt/Unterprojekt (nicht auf ungeplant filtern!)
          if (projectPlanId) {
            try {
              // Lade Subprojekte, um zugehörige IDs zu ermitteln
              const subRes = await apiService.getSubprojects({ status: 'active' });
              const relatedSubIds = new Set((subRes.subprojects || []).filter(sp => sp.project && sp.project.id === Number(projectPlanId)).map(sp => sp.id));
              setProjectRelatedSubIds(relatedSubIds);
              allTickets = allTicketsRaw.filter(t => {
                const belongsToProject = (t.projectId && Number(t.projectId) === Number(projectPlanId)) || (t.subprojectId && relatedSubIds.has(Number(t.subprojectId)));
                const isNotCompleted = t.status !== 'abgeschlossen';
                return belongsToProject && isNotCompleted;
              });
            } catch (e) {
              console.error('Fehler beim Filtern für Projekt-Planungsmodus', e);
            }
          }
          const globalUserEvents = globalEventsResponse.userEvents || {};
          
          // Initialize arrays for each user with their events
          activeUsers.forEach(user => {
            userEventsData[user.email] = globalUserEvents[user.email] || [];
            userTicketsData[user.email] = [];
          });
          
          // Filter tickets by assignedTo user
          allTickets.forEach(ticket => {
            const assignedEmail = ticket.assignedTo;
            if (assignedEmail && userTicketsData[assignedEmail] !== undefined) {
              userTicketsData[assignedEmail].push(ticket);
            }
          });
          
          setUserEvents(userEventsData);
          setUserTickets(userTicketsData);

          // Wenn Projekt-Planungsmodus aktiv: relevante Benutzer automatisch auswählen (basierend auf allen nicht abgeschlossenen Projekttickets)
          try {
            const params = new URLSearchParams(location.search || '');
            const planId = params.get('projectPlanId');
            if (planId) {
              // Stelle sicher, dass verwandte Unterprojekt-IDs vorhanden sind
              let relatedSubIds = new Set();
              try {
                const subRes = await apiService.getSubprojects({ status: 'active' });
                relatedSubIds = new Set((subRes.subprojects || []).filter(sp => sp.project && sp.project.id === Number(planId)).map(sp => sp.id));
                setProjectRelatedSubIds(relatedSubIds);
              } catch (_) {}

              const allTicketsRes = await apiService.getTickets();
              const allTicketsAll = allTicketsRes.tickets || [];
              const relevantEmails = new Set();
              let earliestPlannedStart = null;
              allTicketsAll.forEach(t => {
                try {
                  const belongs = (t.projectId && Number(t.projectId) === Number(planId)) || (t.subprojectId && relatedSubIds.has(Number(t.subprojectId)));
                  const isNotCompleted = t.status !== 'abgeschlossen';
                  if (belongs && isNotCompleted) {
                    if (t.assignedTo) relevantEmails.add(t.assignedTo);
                    if (t.isPlanned && t.plannedStartDateTime) {
                      const s = new Date(t.plannedStartDateTime);
                      if (!isNaN(s)) {
                        if (!earliestPlannedStart || s < earliestPlannedStart) earliestPlannedStart = s;
                      }
                    }
                  }
                } catch (_) {}
              });
              const activeEmails = new Set(activeUsers.map(u => u.email));
              const filteredRelevant = new Set([...relevantEmails].filter(email => activeEmails.has(email)));
              setProjectRelevantUserEmails(filteredRelevant);
              setSelectedUsers(filteredRelevant);

              // Falls es bereits eingeplante Tickets gibt: springe zur ersten Kalenderwoche mit dem frühesten geplanten Ticket
              if (earliestPlannedStart) {
                const d = new Date(earliestPlannedStart);
                // auf Wochenbeginn (Montag) setzen
                const dow = d.getDay();
                const diffToMonday = (dow === 0 ? -6 : 1 - dow);
                const weekStart = new Date(d);
                weekStart.setDate(d.getDate() + diffToMonday);
                weekStart.setHours(0, 0, 0, 0);
                setCurrentDate(weekStart);
                setViewMode('week');

                // Direkt die Daten für diese Woche und die selektierten Benutzer laden,
                // damit die Ansicht nicht erst nach Navigation erscheint
                try {
                  const selectedEmailsArr = Array.from(filteredRelevant);
                  if (selectedEmailsArr.length > 0) {
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekStart.getDate() + (showWeekend ? 6 : 4));
                    weekEnd.setHours(23, 59, 59, 999);

                    const [eventsResp, ticketsResp] = await Promise.all([
                      apiService.getGlobalEvents(selectedEmailsArr, weekStart, weekEnd),
                      apiService.getPlannedTickets(weekStart, weekEnd)
                    ]);

                    const globalEvents = eventsResp.userEvents || {};
                    const weekTickets = ticketsResp.tickets || [];

                    setUserEvents(prev => {
                      const updated = { ...prev };
                      selectedEmailsArr.forEach(email => {
                        updated[email] = globalEvents[email] || [];
                      });
                      return updated;
                    });

                    setUserTickets(prev => {
                      const updated = { ...prev };
                      selectedEmailsArr.forEach(email => { updated[email] = []; });
                      weekTickets.forEach(t => {
                        if (t.assignedTo && updated[t.assignedTo] !== undefined) {
                          updated[t.assignedTo].push(t);
                        }
                      });
                      return updated;
                    });
                  }
                } catch (e) {
                  console.error('Initialer Wochen-Refresh für Projektplanung fehlgeschlagen', e);
                }
              }
            }
          } catch (_) {}
          
        } catch (error) {
          console.error('Error loading global calendar data:', error);
          if (error.response?.status === 401) {
            setError('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
            setTimeout(() => {
              try {
                const raw = String(document.cookie || '');
                const allowAutoSSO = raw.split(';').some(c => c.trim().startsWith('was_logged_in=1'));
                window.location.href = allowAutoSSO ? '/auth/sso' : '/auth/login';
              } catch (_) {
                window.location.href = '/auth/login';
              }
            }, 3000);
          } else {
            setError('Fehler beim Laden der Kalenderdaten. Bitte versuchen Sie es später erneut.');
          }
        } finally {
          setLoading(false);
          setIsInitialLoad(false);
        }
      };
      
      performInitialLoad();
    }
  }, [isInitialLoad, currentDate, selectedUsers.size, viewMode, showWeekend, location.search]);

  

  // Navigation-specific calendar data refresh - uses explicit date
  const loadCalendarDataForDate = useCallback(async (selectedDay) => {
    // Increment token to mark this as the latest navigation/search load
    const token = ++activeNavLoadTokenRef.current;
    try {
      try {
        loadingDebug.start('gcal-events', 'Alle Kalender: Termine laden', 'o365');
        loadingDebug.start('gcal-planned', 'Alle Kalender: Geplante Tickets laden', 'local');
      } catch (_) {}
      // Determine the full date range based on viewMode
      let dayStart, dayEnd;
      if (viewMode === 'day') {
        dayStart = new Date(selectedDay);
        dayStart.setHours(0, 0, 0, 0);
        dayEnd = new Date(selectedDay);
        dayEnd.setHours(23, 59, 59, 999);
      } else {
        const date = new Date(selectedDay);
        const d = date.getDay();
        const diffToMonday = (d === 0 ? -6 : 1 - d);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() + diffToMonday);
        weekStart.setHours(0, 0, 0, 0);
        dayStart = weekStart;
        dayEnd = new Date(weekStart);
        dayEnd.setDate(weekStart.getDate() + (showWeekend ? 6 : 4));
        dayEnd.setHours(23, 59, 59, 999);
      }
      
      // Get selected user emails
      const selectedUserEmails = Array.from(selectedUsers);
      
      if (selectedUserEmails.length === 0) {
        console.log('🌍 [GLOBAL CALENDAR] Navigation refresh skipped - no users selected');
        return;
      }
      
      // Load events and tickets silently
      const [globalEventsResponse, allTicketsResponse] = await Promise.all([
        apiService.getGlobalEvents(selectedUserEmails, dayStart, dayEnd),
        apiService.getPlannedTickets(dayStart, dayEnd)
      ]);
      
      // If a newer load started while we were fetching, abort applying these results
      if (token !== activeNavLoadTokenRef.current) {
        return;
      }

      const allTickets = allTicketsResponse.tickets || [];
      const globalUserEvents = globalEventsResponse.userEvents || {};
      
      // Update events state
      setUserEvents(prevUserEvents => {
        if (token !== activeNavLoadTokenRef.current) return prevUserEvents;
        const updatedUserEvents = { ...prevUserEvents };
        selectedUserEmails.forEach(userEmail => {
          updatedUserEvents[userEmail] = globalUserEvents[userEmail] || [];
        });
        return updatedUserEvents;
      });
      
      // Update tickets state
      setUserTickets(prevUserTickets => {
        if (token !== activeNavLoadTokenRef.current) return prevUserTickets;
        const updatedUserTickets = { ...prevUserTickets };
        selectedUserEmails.forEach(userEmail => {
          updatedUserTickets[userEmail] = [];
        });
        
        // Filter tickets by assignedTo user
        allTickets.forEach(ticket => {
          const assignedEmail = ticket.assignedTo;
          if (selectedUserEmails.includes(assignedEmail) && updatedUserTickets[assignedEmail] !== undefined) {
            updatedUserTickets[assignedEmail].push(ticket);
          }
        });
        
        return updatedUserTickets;
      });
      
      console.log('🌍 [GLOBAL CALENDAR] Navigation refresh completed for date');
      try {
        loadingDebug.done('gcal-events', true);
        loadingDebug.done('gcal-planned', true);
      } catch (_) {}
      
    } catch (error) {
      console.error('🌍 [GLOBAL CALENDAR] Error during navigation refresh:', error);
      try {
        loadingDebug.done('gcal-events', false);
        loadingDebug.done('gcal-planned', false);
      } catch (_) {}
      // On error, just log - don't create circular dependency
      console.error('Navigation refresh failed - user may need to refresh manually');
    }
  }, [selectedUsers, showWeekend, viewMode]);

  // Ensure planned tickets are visible immediately after initial load (entering via header)
  const postInitRefreshDoneRef = useRef(false);
  useEffect(() => {
    if (isInitialLoad) return;
    if (postInitRefreshDoneRef.current) return;
    const hasUsers = Array.isArray(users) && users.length > 0;
    const hasSelection = selectedUsers && selectedUsers.size > 0;
    if (!hasUsers || !hasSelection) return;
    postInitRefreshDoneRef.current = true;
    setTimeout(async () => {
      try { await loadCalendarDataForDate(new Date(currentDate)); } catch (_) {}
    }, 0);
  }, [isInitialLoad, users, selectedUsers, currentDate, loadCalendarDataForDate]);

  // Cleanup effect for resize event listeners (like CalendarView)
  useEffect(() => {
    return () => {
      cleanupResizeListeners();
    };
  }, []);

  // Cleanup selection listeners
  useEffect(() => {
    return () => {
      if (selectionMouseMoveRef.current) {
        document.removeEventListener('mousemove', selectionMouseMoveRef.current);
        selectionMouseMoveRef.current = null;
      }
      if (selectionMouseUpRef.current) {
        document.removeEventListener('mouseup', selectionMouseUpRef.current);
        selectionMouseUpRef.current = null;
      }
    };
  }, []);

  // Optimized function to load data for specific users only
  const loadSpecificUsersData = useCallback(async (userEmails) => {
    console.log('🌍 [GLOBAL CALENDAR] Loading specific users data:', userEmails);
    
    try {
      // Bestimme Zeitbereich abhängig vom aktuellen View-Mode
      let dayStart, dayEnd;
      if (viewMode === 'day') {
        dayStart = new Date(currentDate);
        dayStart.setHours(0, 0, 0, 0);
        dayEnd = new Date(currentDate);
        dayEnd.setHours(23, 59, 59, 999);
      } else {
        // Wochenbereich (Mo–Fr oder Mo–So)
        const today = new Date(currentDate);
        const dow = today.getDay(); // 0=Sun,1=Mon...
        const diffToMonday = (dow === 0 ? -6 : 1 - dow);
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() + diffToMonday);
        weekStart.setHours(0, 0, 0, 0);
        dayStart = weekStart;
        dayEnd = new Date(weekStart);
        dayEnd.setDate(weekStart.getDate() + (showWeekend ? 6 : 4));
        dayEnd.setHours(23, 59, 59, 999);
      }
      
      // Load events and tickets for specific users only
      const [globalEventsResponse, allTicketsResponse] = await Promise.all([
        apiService.getGlobalEvents(userEmails, dayStart, dayEnd),
        apiService.getPlannedTickets(dayStart, dayEnd)
      ]);
      
      const allTickets = allTicketsResponse.tickets || [];
      const globalUserEvents = globalEventsResponse.userEvents || {};
      
      // Update only the specific users in the state
      setUserEvents(prevUserEvents => {
        const updatedUserEvents = { ...prevUserEvents };
        userEmails.forEach(userEmail => {
          updatedUserEvents[userEmail] = globalUserEvents[userEmail] || [];
        });
        return updatedUserEvents;
      });
      
      setUserTickets(prevUserTickets => {
        const updatedUserTickets = { ...prevUserTickets };
        userEmails.forEach(userEmail => {
          updatedUserTickets[userEmail] = [];
        });
        
        // Filter tickets by assignedTo user for specific users only
        allTickets.forEach(ticket => {
          const assignedEmail = ticket.assignedTo;
          if (userEmails.includes(assignedEmail) && updatedUserTickets[assignedEmail] !== undefined) {
            updatedUserTickets[assignedEmail].push(ticket);
          }
        });
        
        return updatedUserTickets;
      });
      
      console.log('🌍 [GLOBAL CALENDAR] Specific users data loaded successfully');
      
    } catch (error) {
      console.error('Error loading specific users data:', error);
      // On error, just log - don't create circular dependency
      console.error('Fallback to full reload needed - user may need to refresh manually');
    }
  }, [currentDate, showWeekend, viewMode]);



  // Load current user once on component mount
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const userResponse = await apiService.getCurrentUser();
        setCurrentUser(userResponse);
      } catch (err) {
        console.error('Error loading current user:', err);
      }
    };
    loadCurrentUser();
  }, []);



  // Save selectedUsers to cookie whenever it changes (Highlight-Modus speichert nicht)
  useEffect(() => {
    if (!projectPlanId && users.length > 0 && !highlightMode) {
      saveSelectedUsersToCookie(selectedUsers);
      console.log('🌍 [GLOBAL CALENDAR] Saved user selection to cookie:', selectedUsers.size, 'users');
    }
  }, [selectedUsers, users.length, projectPlanId, highlightMode]);

  // Background refresh function for silent updates without interrupting user
  const backgroundRefreshCalendarData = useCallback(async () => {
    // Snapshot the current token; if it changes during fetch, skip applying
    const token = activeNavLoadTokenRef.current;
    try {
      // Get current range based on viewMode
      let dayStart, dayEnd;
      if (viewMode === 'day') {
        dayStart = new Date(currentDate);
        dayStart.setHours(0, 0, 0, 0);
        dayEnd = new Date(currentDate);
        dayEnd.setHours(23, 59, 59, 999);
      } else {
        const today = new Date(currentDate);
        const day = today.getDay();
        const diffToMonday = (day === 0 ? -6 : 1 - day);
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() + diffToMonday);
        weekStart.setHours(0, 0, 0, 0);
        dayStart = weekStart;
        dayEnd = new Date(weekStart);
        dayEnd.setDate(weekStart.getDate() + (showWeekend ? 6 : 4));
        dayEnd.setHours(23, 59, 59, 999);
      }
      
      // Get selected user emails
      const selectedUserEmails = Array.from(selectedUsers);
      
      if (selectedUserEmails.length === 0) {
        console.log('🌍 [GLOBAL CALENDAR] Background refresh skipped - no users selected');
        return;
      }
      
      // Load events and tickets silently
      const [globalEventsResponse, allTicketsResponse] = await Promise.all([
        apiService.getGlobalEvents(selectedUserEmails, dayStart, dayEnd),
        apiService.getPlannedTickets(dayStart, dayEnd)
      ]);
      
      // Abort if a newer navigation/search load has started
      if (token !== activeNavLoadTokenRef.current) {
        return;
      }

      const allTickets = allTicketsResponse.tickets || [];
      const globalUserEvents = globalEventsResponse.userEvents || {};
      
      // Check if there are actual changes by comparing with current state
      let hasChanges = false;
      
      // Compare events
      selectedUserEmails.forEach(userEmail => {
        const currentEvents = userEvents[userEmail] || [];
        const newEvents = globalUserEvents[userEmail] || [];
        
        if (currentEvents.length !== newEvents.length) {
          hasChanges = true;
        } else {
          // Compare event IDs and times
          const currentEventMap = new Map(currentEvents.map(e => [e.id, `${e.start?.dateTime}-${e.end?.dateTime}`]));
          const newEventMap = new Map(newEvents.map(e => [e.id, `${e.start?.dateTime}-${e.end?.dateTime}`]));
          
          for (const [id, timeSignature] of newEventMap) {
            if (!currentEventMap.has(id) || currentEventMap.get(id) !== timeSignature) {
              hasChanges = true;
              break;
            }
          }
        }
      });
      
      // Compare tickets
      if (!hasChanges) {
        selectedUserEmails.forEach(userEmail => {
          const currentTickets = userTickets[userEmail] || [];
          const newTickets = allTickets.filter(ticket => ticket.assignedTo === userEmail);
          
          if (currentTickets.length !== newTickets.length) {
            hasChanges = true;
          } else {
            // Compare ticket IDs and times
            const currentTicketMap = new Map(currentTickets.map(t => [t.id, `${t.plannedStartDateTime}-${t.plannedEndDateTime}`]));
            const newTicketMap = new Map(newTickets.map(t => [t.id, `${t.plannedStartDateTime}-${t.plannedEndDateTime}`]));
            
            for (const [id, timeSignature] of newTicketMap) {
              if (!currentTicketMap.has(id) || currentTicketMap.get(id) !== timeSignature) {
                hasChanges = true;
                break;
              }
            }
          }
        });
      }
      
      // Only update state if there are actual changes
      if (hasChanges) {
        console.log('🌍 [GLOBAL CALENDAR] Background refresh detected changes, updating calendar silently...');
        
        // Update events state
        setUserEvents(prevUserEvents => {
          if (token !== activeNavLoadTokenRef.current) return prevUserEvents;
          const updatedUserEvents = { ...prevUserEvents };
          selectedUserEmails.forEach(userEmail => {
            updatedUserEvents[userEmail] = globalUserEvents[userEmail] || [];
          });
          return updatedUserEvents;
        });
        
        // Update tickets state
        setUserTickets(prevUserTickets => {
          if (token !== activeNavLoadTokenRef.current) return prevUserTickets;
          const updatedUserTickets = { ...prevUserTickets };
          selectedUserEmails.forEach(userEmail => {
            updatedUserTickets[userEmail] = [];
          });
          
          // Filter tickets by assignedTo user
          allTickets.forEach(ticket => {
            const assignedEmail = ticket.assignedTo;
            if (selectedUserEmails.includes(assignedEmail) && updatedUserTickets[assignedEmail] !== undefined) {
              updatedUserTickets[assignedEmail].push(ticket);
            }
          });
          
          return updatedUserTickets;
        });
        
        console.log('🌍 [GLOBAL CALENDAR] Background refresh completed with updates');
      } else {
        console.log('🌍 [GLOBAL CALENDAR] Background refresh completed, no changes detected');
      }
      
    } catch (error) {
      console.error('🌍 [GLOBAL CALENDAR] Error during background refresh:', error);
      // Don't fallback to full reload for background refresh to avoid interrupting user
    }
  }, [currentDate, viewMode, showWeekend, selectedUsers, userEvents, userTickets]);

  // Start background refresh every 60 seconds
  useEffect(() => {
    let refreshInterval;
    
    if (users.length > 0 && selectedUsers.size > 0) {
      refreshInterval = setInterval(async () => {
        // Only refresh when page is visible and we have users
        if (document.visibilityState === 'visible' && !operationInProgress) {
          await backgroundRefreshCalendarData();
        }
      }, 60000); // 60 seconds for frequent updates
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [users, selectedUsers, backgroundRefreshCalendarData, operationInProgress]);

  // Update current time every 10 minutes for time marker
  useEffect(() => {
    // Initial time set
    setCurrentTime(new Date());
    
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(timeInterval);
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      // For manual refresh, reload the calendar data
      console.log('🌍 [GLOBAL CALENDAR] Manual refresh: reloading calendar data');
      
      // Trigger full reload by resetting initial load state
      setIsInitialLoad(true);
    } catch (error) {
      console.error('🌍 [GLOBAL CALENDAR] Manual refresh error:', error);
      setError(t('global.refreshError', 'Fehler beim Aktualisieren des globalen Kalenders'));
    } finally {
      setLoading(false);
    }
  };

  // Pure sidebar refresh function - only for TicketSidebar updates (no calendar impact)
  const handleSidebarOnlyRefresh = () => {
    // This function is called by TicketSidebar as onRefresh prop
    // TicketSidebar will handle its own refresh via loadUnplannedTickets()
    // We don't need to do anything calendar-related here
    console.log('🌍 [GLOBAL CALENDAR] TicketSidebar refresh triggered (no calendar update needed)');
  };

  // Navigation functions
  const navigateDay = async (direction) => {
    let newDate;
    if (viewMode === 'day') {
      // Day mode: jump by single days
      newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + direction);
    } else {
      // Week modes: jump whole weeks from week start (Monday)
      const base = new Date(currentDate);
      const day = base.getDay();
      const diffToMonday = (day === 0 ? -6 : 1 - day);
      const weekStart = new Date(base);
      weekStart.setDate(base.getDate() + diffToMonday);
      weekStart.setDate(weekStart.getDate() + direction * 7);
      newDate = weekStart;
    }
    setCurrentDate(newDate);
    
    if (users.length > 0 && selectedUsers.size > 0 && !isInitialLoad) {
      setTimeout(async () => {
        await loadCalendarDataForDate(newDate);
      }, 10);
    }
  };

  const goToToday = async () => {
    const newDate = new Date();
    setCurrentDate(newDate);
    
    // Use optimized navigation refresh if users are already loaded
    if (users.length > 0 && selectedUsers.size > 0 && !isInitialLoad) {
      // Load calendar data for today directly
      setTimeout(async () => {
        await loadCalendarDataForDate(newDate);
      }, 10);
    }
  };

  // Drag & Drop functions
  // Live Drag Preview berechnen (improved like CalendarView)
  const calculateDragPreview = (e, userEmail) => {
    if (!draggedTicket && !draggedPlannedTicket) return null;
    
    const userColumn = e.currentTarget.querySelector('.user-day-section');
    if (!userColumn) return null;
    
    const rect = userColumn.getBoundingClientRect();
    
    // Berechne Position basierend auf Oberkante des Elements, nicht auf Mausposition
    let relativeY = e.clientY - rect.top;
    
    // Für geplante Tickets: Verwende den Offset zur Oberkante
    if (draggedPlannedTicket && dragStartPos) {
      relativeY = e.clientY - rect.top - dragStartPos.y;
    }
    
    const topPercent = Math.max(0, Math.min(100, (relativeY / rect.height) * 100));
    
    // Konvertiere zu Minuten (8-18 Uhr = 600 Minuten)
    const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
    const totalMinutes = (topPercent / 100) * workingDayMinutes;
    const snappedMinutes = snapToGrid(totalMinutes);
    
    // Berechne neue Startzeit
    const newStartHour = Math.floor(snappedMinutes / 60) + timeStartHour;
    const newStartMinute = snappedMinutes % 60;
    
    const newStartTime = `${newStartHour.toString().padStart(2, '0')}:${newStartMinute.toString().padStart(2, '0')}`;
    
    // Ghost-Höhe anhand geplanter Stunden oder 60 Minuten
    let durationMinutes = 60;
    if (draggedTicket && draggedTicket.plannedHours) {
      const ph = parseFloat(draggedTicket.plannedHours);
      if (!Number.isNaN(ph) && ph > 0) {
        durationMinutes = Math.round(ph * 60);
      }
    } else if (draggedPlannedTicket) {
      try {
        const s = new Date(draggedPlannedTicket.plannedStartDateTime);
        const en = new Date(draggedPlannedTicket.plannedEndDateTime);
        const diff = Math.max(0, (en - s) / (1000 * 60));
        if (diff > 0) durationMinutes = diff;
      } catch (_) {}
    }
    const heightPercent = Math.max(2, Math.min(100, (durationMinutes / workingDayMinutes) * 100));
    
    return {
      user: users.find(u => u.email === userEmail)?.displayName || userEmail,
      startTime: newStartTime,
      position: { x: e.clientX, y: e.clientY },
      topPercent,
      heightPercent
    };
  };

  // ===== CREATE VIA CALENDAR SELECTION (GLOBAL) =====
  const handleSelectionStart = (e, userEmail, day) => {
    // Only left-click, block during operations, ignore when clicking on items
    if (operationInProgress || e.button !== 0) return;
    if (e.target.closest && (
      e.target.closest('.global-calendar-ticket') ||
      e.target.closest('.global-calendar-event') ||
      e.target.closest('.calendar-ticket') ||
      e.target.closest('.calendar-event') ||
      e.target.closest('.resize-handle')
    )) return;

    const section = e.currentTarget; // .user-day-section
    const rect = section.getBoundingClientRect();

    setIsSelecting(true);
    setSelectionUserEmail(userEmail);
    setSelectionDay(day);
    selectionContainerRectRef.current = rect;
    selectionStartYRef.current = e.clientY - rect.top;
    setSelectionRect({ topPercent: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)), heightPercent: 0 });

    const moveHandler = (moveEvent) => {
      if (!selectionContainerRectRef.current) return;
      moveEvent.preventDefault();
      const moveRect = selectionContainerRectRef.current;
      const currentY = moveEvent.clientY - moveRect.top;
      const startY = selectionStartYRef.current;
      const top = Math.max(0, Math.min(startY, currentY));
      const bottom = Math.max(startY, currentY);
      const topPercent = Math.max(0, Math.min(100, (top / moveRect.height) * 100));
      const heightPercent = Math.max(0, Math.min(100, ((bottom - top) / moveRect.height) * 100));
      setSelectionRect({ topPercent, heightPercent });
    };

    const upHandler = (upEvent) => {
      if (!selectionContainerRectRef.current) return;
      document.removeEventListener('mousemove', selectionMouseMoveRef.current);
      document.removeEventListener('mouseup', selectionMouseUpRef.current);
      selectionMouseMoveRef.current = null;
      selectionMouseUpRef.current = null;

      const finRect = selectionContainerRectRef.current;
      const endY = upEvent.clientY - finRect.top;
      const startY = selectionStartYRef.current;
      const topPx = Math.max(0, Math.min(startY, endY));
      const bottomPx = Math.max(startY, endY);

      const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
      const startPercent = Math.max(0, Math.min(100, (topPx / finRect.height) * 100));
      const endPercent = Math.max(0, Math.min(100, (bottomPx / finRect.height) * 100));
      let startTotalMinutes = (startPercent / 100) * workingDayMinutes;
      let endTotalMinutes = (endPercent / 100) * workingDayMinutes;

      // snap to 30 minutes
      startTotalMinutes = snapToGrid(startTotalMinutes);
      endTotalMinutes = snapToGrid(endTotalMinutes);

      // Ensure minimum duration 30 minutes
      if (endTotalMinutes <= startTotalMinutes) {
        endTotalMinutes = startTotalMinutes + 30;
      }

      const startHour = Math.floor(startTotalMinutes / 60) + timeStartHour;
      const startMinute = startTotalMinutes % 60;
      const endHour = Math.floor(endTotalMinutes / 60) + timeStartHour;
      const endMinute = endTotalMinutes % 60;

      const startDateTime = new Date(day);
      startDateTime.setHours(startHour, startMinute, 0, 0);
      const endDateTime = new Date(day);
      endDateTime.setHours(endHour, endMinute, 0, 0);

      setIsSelecting(false);
      setSelectionRect(null);
      selectionContainerRectRef.current = null;

      setPendingPlannedTimeRange({
        startDateTime: startDateTime.toISOString(),
        endDateTime: endDateTime.toISOString(),
        userEmail
      });

      // open create ticket modal
      setShowCreateTicketForm(true);
    };

    selectionMouseMoveRef.current = moveHandler;
    selectionMouseUpRef.current = upHandler;
    document.addEventListener('mousemove', moveHandler, { passive: false });
    document.addEventListener('mouseup', upHandler);
  };

  // Drag & Drop Funktionen (improved like CalendarView)
  const handleDragOver = (e, userEmail, day) => {
    e.preventDefault();
    
    // Block drag over if operation is in progress
    if (operationInProgress) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell({ userEmail, day });
    
    // Berechne und zeige Live-Feedback
    const preview = calculateDragPreview(e, userEmail);
    if (preview) {
      setDragPreview(preview);
      setShowDragPreview(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverCell(null);
    setShowDragPreview(false);
  };

  const handleDropOnUser = async (e, userEmail, targetDay = null) => {
    e.preventDefault();
    setDragOverCell(null);
    setShowDragPreview(false);
    
    // Block drop if operation is already in progress
    if (operationInProgress) {
      console.log('🚫 Drop blocked - operation already in progress');
      return;
    }
    
    // Ticket- und Drop-Kontext ermitteln
    let dragData;
    try { dragData = JSON.parse(e.dataTransfer.getData('text/plain')); } catch (_) { dragData = null; }
    if (dragData && dragData.type === 'planned-ticket') {
      try {
        const userColumn = e.currentTarget.querySelector('.user-day-section');
        const rect = userColumn.getBoundingClientRect();
        let relativeY = e.clientY - rect.top;
        if (dragStartPos) relativeY = e.clientY - rect.top - dragStartPos.y;
        const heightPercent = Math.max(0, Math.min(100, (relativeY / rect.height) * 100));
        const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
        const totalMinutes = (heightPercent / 100) * workingDayMinutes;
        const snappedMinutes = snapToGrid(totalMinutes);
        const newStartHour = Math.floor(snappedMinutes / 60) + timeStartHour;
        const newStartMinute = snappedMinutes % 60;
        const baseDate = targetDay ? new Date(targetDay) : new Date(currentDate);
        const newStartDateTime = new Date(baseDate);
        newStartDateTime.setHours(newStartHour, newStartMinute, 0, 0);
        setDropChoiceModal({ visible: true, context: { source: 'global', ticket: dragData.ticket, userEmail, targetDay: baseDate, newStartDateTime, origin: (dragData && dragData.origin) || null } });
        return;
      } catch (_) {}
    }

    // Set operation in progress (Standardfluss)
    setOperationInProgress(true);
    setOperationMessage(t('global.movingTicket', 'Ticket wird verschoben...'));
    
    try {
      JSON.parse(e.dataTransfer.getData('text/plain')); // Validierung: wirft bei fremden Drop-Daten (Ergebnis wird nicht mehr gelesen)
      
      // Get current assigned user for later use
      let currentAssignedUser = null;
      if (dragData.type === 'planned-ticket') {
        currentAssignedUser = dragData.ticket.assignedTo;
      }
      
      // Berechne Drop-Position für beide Ticket-Typen
      const userColumn = e.currentTarget.querySelector('.user-day-section');
      const rect = userColumn.getBoundingClientRect();
      
      // Berechne Position basierend auf Oberkante des Elements, nicht auf Mausposition
      let relativeY = e.clientY - rect.top;
      
      // Für geplante Tickets: Verwende den Offset zur Oberkante
      if (dragData.type === 'planned-ticket' && dragStartPos) {
        relativeY = e.clientY - rect.top - dragStartPos.y;
      }
      
      const heightPercent = Math.max(0, Math.min(100, (relativeY / rect.height) * 100));
      
      // Konvertiere zu Minuten (8-18 Uhr = 600 Minuten)
      const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
      const totalMinutes = (heightPercent / 100) * workingDayMinutes;
      const snappedMinutes = snapToGrid(totalMinutes);
      
      // Berechne neue Startzeit
      const newStartHour = Math.floor(snappedMinutes / 60) + timeStartHour;
      const newStartMinute = snappedMinutes % 60;
      
      if (dragData.type === 'ticket') {
        // Unplanned ticket being dropped
        const ticket = dragData.ticket;
        
        // Duration: planned hours if present, else 1 hour
        const ph = parseFloat(ticket?.plannedHours);
        const durationMs = (!Number.isNaN(ph) && ph > 0) ? Math.round(ph * 60 * 60 * 1000) : (60 * 60 * 1000);
        
        // Set new times
        const baseDate = targetDay ? new Date(targetDay) : new Date(currentDate);
        const newStartDateTime = new Date(baseDate);
        newStartDateTime.setHours(newStartHour, newStartMinute, 0, 0);
        
        const newEndDateTime = new Date(newStartDateTime.getTime() + durationMs);
        
        // Plan ticket with calendar in target user's calendar
        const planningData = {
          startDateTime: newStartDateTime.toISOString(),
          endDateTime: newEndDateTime.toISOString(),
          assignedTo: userEmail
        };

        try {
          await apiService.planTicketForUser(ticket.id, planningData, userEmail);
          console.log(`✅ Successfully planned ticket ${ticket.ticketNumber} for user ${userEmail}`);
          
          // Dynamically remove ticket from sidebar for immediate UI feedback
          if (removeTicketFromSidebar.current?.removeTicket) {
            removeTicketFromSidebar.current.removeTicket(ticket.id);
            console.log('🌍 [GLOBAL CALENDAR] Dynamically removed ticket from sidebar');
          }
          
          // Immediate UI refresh for affected users to prevent disappearing items
          await loadSpecificUsersData([userEmail]);
          try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'planned_from_global' } })); } catch (_) {}
          
        } catch (error) {
          console.error('Error planning ticket for user:', error);
          
          // Spezifische Fehlerbehandlung für Kalender-Berechtigungen
          if (error.response?.data?.code === 'CALENDAR_PERMISSION_DENIED') {
            
            // Prüfe ob Fallback verwendet wurde
            if (error.response?.data?.fallbackUsed) {
              setError(`⚠️ Teilweise erfolgreich: Ticket wurde ${userEmail} zugewiesen, aber Kalendereintrag wurde in IHREM Kalender erstellt (Berechtigung für ${userEmail}'s Kalender fehlt).

Lösungen:
1. Azure App: 'Calendars.ReadWrite.Shared' Permission hinzufügen
2. ${userEmail}: Kalender-Berechtigung erteilen
3. Kalendereintrag manuell zu ${userEmail} verschieben`);
              // Trotzdem weitermachen - Ticket wurde zugewiesen
            } else {
              setError(`⚠️ Kalender-Berechtigung fehlt: 

${error.response?.data?.details || error.message}

Ticket wurde NICHT zugewiesen.`);
              return; // Verhindere die Zuweisung
            }
          } else if (error.response?.data?.code === 'USER_NOT_FOUND') {
            setError(t('global.userNotFound', '⚠️ Benutzer {{userEmail}} wurde nicht gefunden oder der Kalender ist nicht verfügbar.', { userEmail }));
            return; // Verhindere die Zuweisung
          } else {
            setError(`Fehler beim Planen des Tickets: ${error.response?.data?.details || error.message}`);
            return; // Verhindere die Zuweisung
          }
        }

        // Optimized refresh - only reload affected users instead of all users
        console.log('🌍 [GLOBAL CALENDAR] Optimized reload after cross-user planning...');
        
        // Only reload specific users that were affected by the operation
        const affectedUsers = [userEmail];
        
        // Partial reload instead of full loadGlobalCalendarData()
        await loadSpecificUsersData(affectedUsers);
        try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'moved_in_global' } })); } catch (_) {}
      } else if (dragData.type === 'planned-ticket') {
        // Planned ticket being moved to different user
        const ticket = dragData.ticket;
        
        // Calculate duration of original ticket
        const originalStart = new Date(ticket.plannedStartDateTime);
        const originalEnd = new Date(ticket.plannedEndDateTime);
        const duration = originalEnd - originalStart;
        
        // Set new times
        const baseDate = targetDay ? new Date(targetDay) : new Date(currentDate);
        const newStartDateTime = new Date(baseDate);
        newStartDateTime.setHours(newStartHour, newStartMinute, 0, 0);
        
        const newEndDateTime = new Date(newStartDateTime.getTime() + duration);
        
        // Check if we're moving to a different user
        const isMovingToSameUser = currentAssignedUser === userEmail;
        
        if (isMovingToSameUser) {
          // Same user - just update timing
          const planningUpdateData = {
            startDateTime: newStartDateTime.toISOString(),
            endDateTime: newEndDateTime.toISOString(),
            isPlanned: true
          };

          await apiService.updateTicketPlanning(ticket.id, planningUpdateData);
        } else {
          // Different user - need to create new calendar entry in target user's calendar
          // First unplan the ticket to remove from current user's calendar
          try {
            await apiService.unplanTicket(ticket.id);
            console.log(`🗑️ Removed ticket ${ticket.ticketNumber} from ${currentAssignedUser}'s calendar`);
          } catch (error) {
            console.warn('Warning: Could not unplan ticket from previous user:', error);
          }
          
          // Then plan it for the new user
          const planningData = {
            startDateTime: newStartDateTime.toISOString(),
            endDateTime: newEndDateTime.toISOString(),
            assignedTo: userEmail
          };

          try {
            await apiService.planTicketForUser(ticket.id, planningData, userEmail);
            console.log(`✅ Successfully moved ticket ${ticket.ticketNumber} to user ${userEmail}'s calendar`);
            
            // Immediate UI refresh for affected users
            await loadSpecificUsersData([userEmail]);
            
          } catch (error) {
            console.error('Error moving ticket to new user:', error);
            
            // Spezifische Fehlerbehandlung für Kalender-Berechtigungen
            if (error.response?.data?.code === 'CALENDAR_PERMISSION_DENIED') {
              setError(t('global.permissionDenied', '⚠️ Keine Berechtigung: Sie können keine Kalendereinträge für {{userEmail}} erstellen. Benötigen erweiterte Kalenderberechtigungen.', { userEmail }));
              return; // Verhindere die Bewegung
            } else if (error.response?.data?.code === 'USER_NOT_FOUND') {
              setError(t('global.userNotFound', '⚠️ Benutzer {{userEmail}} wurde nicht gefunden oder der Kalender ist nicht verfügbar.', { userEmail }));
              return; // Verhindere die Bewegung
            } else {
              setError(t('global.moveTicketError', 'Fehler beim Verschieben des Tickets: {{details}}', { details: error.response?.data?.details || error.message }));
              return; // Verhindere die Bewegung
            }
          }
        }

        // Optimized refresh - only reload affected users
        console.log('🌍 [GLOBAL CALENDAR] Optimized reload after cross-user move...');
        
        // Only reload users affected by the move operation
        const affectedUsers = [userEmail];
        if (currentAssignedUser && currentAssignedUser !== userEmail) {
          affectedUsers.push(currentAssignedUser);
        }
        
        await loadSpecificUsersData(affectedUsers);

        // If origin was sidebar (overdue planned ticket dragged from sidebar), remove it from sidebar immediately
        try {
          if (dragData && dragData.origin === 'sidebar' && removeTicketFromSidebar.current?.removeTicket) {
            removeTicketFromSidebar.current.removeTicket(ticket.id);
            console.log('🌍 [GLOBAL CALENDAR] Removed moved planned ticket from sidebar (origin sidebar)');
          }
        } catch (_) {}
      }
    } catch (error) {
      console.error('Error handling drop:', error);
      setError(t('global.placeTicketError', 'Fehler beim Platzieren des Tickets'));
    } finally {
      // Always reset operation state
      setOperationInProgress(false);
      setOperationMessage('');
    }
  };

  // Ticket click handlers  
  const handleTicketClick = async (e, ticket) => {
    // Don't open modal if we just finished resizing
    if (justResized) {
      setJustResized(false);
      return;
    }
    
    // Don't open modal if clicking on resize handles
    if (e.target.classList.contains('resize-handle')) {
      return;
    }
    
    // Optimistically remove red corner immediately
    setUserTickets(currentUserTickets => {
      const updatedUserTickets = { ...currentUserTickets };
      Object.keys(updatedUserTickets).forEach(userEmail => {
        updatedUserTickets[userEmail] = updatedUserTickets[userEmail].map(t => 
          t.id === ticket.id ? { ...t, hasNewEmails: false, hasNewComments: false, hasNewAttachments: false, isNewTicket: false, hasNewActivity: false } : t
        );
      });
      return updatedUserTickets;
    });
    
    // Open modal immediately
    setSelectedTicket(ticket);
    setShowTicketDetail(true);
    
    // Mark ticket as viewed in background
    try {
      await apiService.markTicketAsViewed(ticket.id);
    } catch (error) {
      console.error('Error marking ticket as viewed:', error);
      
      // If API call fails, show red corner again
      setUserTickets(currentUserTickets => {
        const updatedUserTickets = { ...currentUserTickets };
        Object.keys(updatedUserTickets).forEach(userEmail => {
          updatedUserTickets[userEmail] = updatedUserTickets[userEmail].map(t => 
            t.id === ticket.id ? { ...t, hasNewActivity: true } : t
          );
        });
        return updatedUserTickets;
      });
    }
  };

  const handleTicketDetailClose = async (hasChanges = false) => {
    setShowTicketDetail(false);
    const ticketId = selectedTicket?.id;
    const assignedUser = selectedTicket?.assignedTo;
    setSelectedTicket(null);
    
    // Handle different types of changes
    if (hasChanges === 'status_uncompleted') {
      console.log('🌍 [GLOBAL CALENDAR] Ticket status changed from abgeschlossen, adding back to sidebar...');
      
      // Dynamically add ticket back to sidebar using special function for completed->other status changes
      if (removeTicketFromSidebar.current?.addTicketBackFromCompleted && ticketId) {
        await removeTicketFromSidebar.current.addTicketBackFromCompleted(ticketId);
        console.log('🌍 [GLOBAL CALENDAR] Dynamically added uncompleted ticket back to sidebar');
      }
      
      // Refresh the calendar with affected users only (if we know the assigned user)
      if (assignedUser) {
        await loadSpecificUsersData([assignedUser]);
      } else {
        // Fallback to full refresh if no specific user known
        await handleRefresh();
      }
    } else if (hasChanges === 'completed') {
      console.log('🌍 [GLOBAL CALENDAR] Ticket was completed, refreshing affected user data...');
      // Entferne sofort aus Sidebar (falls vorhanden)
      try {
        if (removeTicketFromSidebar.current?.removeTicket && ticketId) {
          removeTicketFromSidebar.current.removeTicket(ticketId);
          console.log('🌍 [GLOBAL CALENDAR] Removed completed ticket from sidebar');
        }
      } catch (_) {}
      if (assignedUser) {
        await loadSpecificUsersData([assignedUser]);
      } else {
        await handleRefresh();
      }
    } else if (hasChanges === true) {
      console.log('🌍 [GLOBAL CALENDAR] Ticket was modified, performing optimized refresh...');
      
      // Refresh the calendar with affected users only (if we know the assigned user)
      if (assignedUser) {
        await loadSpecificUsersData([assignedUser]);
      } else {
        // Fallback to full refresh if no specific user known
        await handleRefresh();
      }
    } else {
      console.log('🌍 [GLOBAL CALENDAR] Ticket was only viewed, no refresh needed');
    }
  };

  const handleTicketUpdate = useCallback(async (ticketId = null) => {
    // Refresh calendar after ticket update
    await handleRefresh();
    
    // If we have a specific ticket ID or a selected ticket, update it with the latest data
    const targetTicketId = ticketId || selectedTicket?.id;
    if (targetTicketId) {
      try {
        const response = await apiService.getTickets();
        const updatedTicket = response.tickets.find(t => t.id === targetTicketId);
        if (updatedTicket) {
          setSelectedTicket(updatedTicket);
        }
      } catch (error) {
        console.error('Error updating selected ticket:', error);
      }
    }
  }, [selectedTicket]);

  // Handle ticket creation
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (isCreatingTicket) return;
    
    if (!newTicket.title.trim()) {
      flagInvalidField(e.currentTarget, '#ticket-title', t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.')) || pushToast({ type: 'error', message: t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.') });
      return;
    }

    try {
      setIsCreatingTicket(true);
      // Build payload and pre-assign if created via selection
      const newTicketPayload = { ...newTicket };
      if (pendingPlannedTimeRange?.userEmail) {
        newTicketPayload.assignedTo = pendingPlannedTimeRange.userEmail;
      }

      // Create ticket and get the response
      const response = await apiService.createTicket(newTicketPayload);
      
      // Extract created ticket from response (flexible handling)
      const createdTicket = response.ticket || response;
      
      // If created via selection, immediately plan into selected user's calendar
      if (pendingPlannedTimeRange) {
        const planningData = {
          startDateTime: pendingPlannedTimeRange.startDateTime,
          endDateTime: pendingPlannedTimeRange.endDateTime,
          assignedTo: pendingPlannedTimeRange.userEmail
        };

        try {
          // Prefer dedicated API if exists, else fallback to update/planTicket
          if (apiService.planTicketForUser) {
            await apiService.planTicketForUser(createdTicket.id, planningData, pendingPlannedTimeRange.userEmail);
          } else {
            await apiService.planTicket(createdTicket.id, planningData);
          }

          // Clear selection state and refresh only the affected user
          const affectedUser = pendingPlannedTimeRange.userEmail;
          setPendingPlannedTimeRange(null);

          // Close modal and reset form
          setShowCreateTicketForm(false);
          setNewTicket({
            title: '',
            description: '',
            priority: 'normal',
            assignedTo: '',
            category: '',
            tags: [],
            calenderMarker: null,
            customerId: null,
            contactId: null
          });

          await loadSpecificUsersData([affectedUser]);

        } catch (planErr) {
          console.error('Error planning newly created ticket in global view:', planErr);
          setError(t('global.planNewTicketError', 'Fehler beim Planen des neu erstellten Tickets'));
        }
      } else {
        // Construct the full ticket object for dynamic sidebar update
        const ticketForSidebar = {
          id: createdTicket.id,
          ticketNumber: createdTicket.ticketNumber,
          title: newTicket.title,
          description: newTicket.description || '',
          priority: newTicket.priority || 'normal',
          category: newTicket.category || '',
          assignedTo: newTicket.assignedTo || '',
          status: 'offen',
          isPlanned: false,
          tags: newTicket.tags || [],
          calenderMarker: newTicket.calenderMarker || null,
          __forceVisible: true,
          hasNewActivity: true,
          isNewTicket: true,
          createdAt: new Date().toISOString(),
          ...createdTicket
        };
        
        setShowCreateTicketForm(false);
        setNewTicket({
          title: '',
          description: '',
          priority: 'normal',
          assignedTo: '',
          category: '',
          tags: [],
          calenderMarker: null,
          customerId: null,
          contactId: null
        });
        
        // Dynamically add the new ticket to the sidebar (no API call)
        console.log('🌍 [GLOBAL CALENDAR] Dynamically adding newly created ticket to sidebar');
        if (removeTicketFromSidebar.current?.addNewTicket) {
          removeTicketFromSidebar.current.addNewTicket(ticketForSidebar);
        }
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      pushToast({ type: 'error', message: t('global.createTicketError', 'Fehler beim Erstellen des Tickets.') });
    } finally {
      setIsCreatingTicket(false);
    }
  };

  // Handle opening create ticket form
  const handleCreateTicketClick = () => {
    setShowCreateTicketForm(true);
  };
  
  // Close modal helper (clears selection planning state)
  const closeCreateTicketModal = () => {
    setShowCreateTicketForm(false);
    setPendingPlannedTimeRange(null);
  };

  // Load available emails for ticket assignment
  const loadAvailableEmails = async () => {
    try {
      const response = await apiService.getAvailableEmails();
      setAvailableEmails(response.emails || []);
    } catch (error) {
      console.error('Error loading available emails:', error);
    }
  };

  // Load available emails when create form opens
  useEffect(() => {
    if (showCreateTicketForm) {
      loadAvailableEmails();
    }
  }, [showCreateTicketForm]);

  // ===== CONTEXT MENU HANDLERS =====
  const handleTicketRightClick = (e, ticket) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      ticket: ticket
    });
  };

  // #119 (W7): auf den ersten freien Arbeitszeit-Slot der nächsten Woche
  // verschieben (Slot-Suche macht der Server, Route /plan-next-week)
  const handleMoveToNextWeek = async () => {
    if (!contextMenu.ticket) return;
    if (operationInProgress) return;
    const ticketId = contextMenu.ticket.originalTicketId || contextMenu.ticket.id;
    setContextMenu({ visible: false, x: 0, y: 0, ticket: null });
    setOperationInProgress(true);
    setOperationMessage(t('contextMenu.movingNextWeek', 'Wird auf nächste Woche verschoben...'));
    try {
      const resp = await apiService.planTicketNextWeek(ticketId);
      const start = resp?.plannedStartDateTime ? new Date(resp.plannedStartDateTime) : null;
      const when = start ? start.toLocaleString([], { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
      pushToast({ message: t('contextMenu.movedNextWeek', `Verschoben auf ${when}`, { date: when }), type: 'success' });
      try {
        await loadCalendarDataForDate(new Date(currentDate));
      } catch (_) {}
    } catch (error) {
      console.error('Error planning ticket for next week:', error);
      const serverMsg = error?.response?.data?.error;
      pushToast({ message: serverMsg || t('contextMenu.moveNextWeekError', 'Verschieben auf nächste Woche fehlgeschlagen.'), type: 'error' });
    } finally {
      setOperationInProgress(false);
      setOperationMessage('');
    }
  };

  const handleRemoveFromPlanning = async () => {
    if (!contextMenu.ticket) return;
    
    // Block if operation is already in progress
    if (operationInProgress) {
      console.log('🚫 Remove from planning blocked - operation already in progress');
      return;
    }
    
    const ticket = contextMenu.ticket;
    const assignedUser = ticket.assignedTo;
    
    // Set operation in progress
    setOperationInProgress(true);
    setOperationMessage(t('global.removingFromPlanning', 'Ticket wird aus Planung entfernt...'));
    
    try {
      await apiService.unplanTicket(ticket.id);
      
      // Dynamically add ticket back to sidebar for immediate UI feedback (force visible)
      if (removeTicketFromSidebar.current?.addNewTicket) {
        const forcedTicket = {
          ...ticket,
          isPlanned: false,
          __forceVisible: true,
          hasNewActivity: true,
          isNewTicket: true,
          createdAt: new Date().toISOString()
        };
        removeTicketFromSidebar.current.addNewTicket(forcedTicket);
        console.log('🌍 [GLOBAL CALENDAR] Dynamically added ticket back to sidebar (force-visible)');
      } else if (removeTicketFromSidebar.current?.addTicket) {
        await removeTicketFromSidebar.current.addTicket(ticket.id);
        console.log('🌍 [GLOBAL CALENDAR] Dynamically added ticket back to sidebar (fallback)');
      }
      
      // Immediate UI refresh for affected users
      if (ticket?.assignedTo) {
        await loadSpecificUsersData([ticket.assignedTo]);
      }
      
      // Close context menu
      setContextMenu({ visible: false, x: 0, y: 0, ticket: null });
      
      // Optimized refresh - only reload affected user
      if (assignedUser) {
        await loadSpecificUsersData([assignedUser]);
      } else {
        // Fallback to full refresh if no specific user
        await handleRefresh();
      }
      
      // Additional success message for the user
      console.log(`✅ Ticket ${ticket.ticketNumber} wurde aus der Planung entfernt. Die Zuordnung bleibt unverändert.`);
      try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'unplanned_from_global' } })); } catch (_) {}

      // Stelle sicher, dass der betroffene Benutzer in den ausgewählten Benutzern enthalten ist,
      // sodass sein Toggle in der Sidebar sichtbar ist und das Ticket sofort erscheint,
      // sobald dieser Benutzer in der Sidebar ausgewählt wird
      if (assignedUser) {
        setSelectedUsers(prev => {
          const next = new Set(prev);
          if (!next.has(assignedUser)) next.add(assignedUser);
          return next;
        });
      }
      
    } catch (error) {
      console.error('Error removing ticket from planning:', error);
      setError(t('global.removePlanningError', 'Fehler beim Entfernen des Tickets aus der Planung'));
    } finally {
      // Always reset operation state
      setOperationInProgress(false);
      setOperationMessage('');
    }
  };

  // ===== DRAG & DROP FÜR GEPLANTE TICKETS (improved like CalendarView) =====
  const handlePlannedTicketDragStart = (e, ticket) => {
    // Verhindere Drag-Start, wenn wir im Resize-Modus sind oder auf einem Resize-Handle geklickt wird
    if (resizeMode || e.target.classList.contains('resize-handle')) {
      e.preventDefault();
      return;
    }
    
    // Block drag start if operation is in progress
    if (operationInProgress) {
      e.preventDefault();
      console.log('🚫 Drag blocked - operation in progress:', operationMessage);
      return;
    }
    
    setDraggedPlannedTicket(ticket);
    
    // Speichere die Maus-Position relativ zum Element
    const rect = e.currentTarget.getBoundingClientRect();
    setDragStartPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({
      type: 'planned-ticket',
      ticket: ticket
    }));
  };

  const handlePlannedTicketDragEnd = () => {
    setDraggedPlannedTicket(null);
    setDragStartPos(null);
    setShowDragPreview(false);
  };

  // ===== RESIZE FUNCTIONS (improved like CalendarView) =====
  const handleResizeStart = (e, ticket, handle) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Block resize if operation is in progress
    if (operationInProgress) {
      console.log('🚫 Resize blocked - operation in progress:', operationMessage);
      return;
    }
    
    // Clean up any existing listeners, but don't reset the state yet
    cleanupResizeListeners();
    
    // Aktiviere Resize-Modus
    setResizeMode(true);
    
    setResizingTicket(ticket);
    setResizeHandle(handle);
    const startPos = { x: e.clientX, y: e.clientY };
    setResizeStartPos(startPos);
    setResizeStartHeight(e.currentTarget.parentElement.offsetHeight);
    setShowResizePreview(true);
    setJustResized(false);
    
    // Initialize resize values with current ticket values
    currentResizeValues.current = {
      startDateTime: ticket.plannedStartDateTime,
      endDateTime: ticket.plannedEndDateTime
    };
    
    // Create bound functions for event listeners with ticket and handle in closure
    const boundHandleResizeMove = (e) => {
      handleResizeMoveWithTicket(e, ticket, handle, startPos);
    };
    const boundHandleResizeEnd = (e) => {
      handleResizeEndWithTicket(e, ticket, handle);
    };
    
    // Store references for cleanup
    resizeMouseMoveRef.current = boundHandleResizeMove;
    resizeMouseUpRef.current = boundHandleResizeEnd;
    
    // Add event listeners
    document.addEventListener('mousemove', boundHandleResizeMove, { passive: false });
    document.addEventListener('mouseup', boundHandleResizeEnd);
  };

  const handleResizeMoveWithTicket = (e, ticket, handle, startPos) => {
    if (!ticket || !handle) return;
    
    e.preventDefault();
    
    const deltaY = e.clientY - startPos.y;
    const workingDayMinutes = 10 * 60; // 10 hours from 8 to 18
    
    // Get actual height of the user-day-section dynamically
    const userDaySection = document.querySelector('.user-day-section');
    const userDayHeight = userDaySection ? userDaySection.offsetHeight : 600;
    
    const minutesPerPixel = workingDayMinutes / userDayHeight;
    const deltaMinutes = deltaY * minutesPerPixel;
    
    const originalStart = new Date(ticket.plannedStartDateTime);
    const originalEnd = new Date(ticket.plannedEndDateTime);
    
    let newStartDateTime, newEndDateTime;
    
    if (handle === 'top') {
      // Resizing from top - change start time with 30-minute snapping
      const newStartMinutes = originalStart.getHours() * 60 + originalStart.getMinutes() + deltaMinutes;
      const snappedStartMinutes = snapToGrid(newStartMinutes);
      
      newStartDateTime = new Date(originalStart);
      newStartDateTime.setHours(Math.floor(snappedStartMinutes / 60), snappedStartMinutes % 60, 0, 0);
      newEndDateTime = originalEnd;
    } else if (handle === 'bottom') {
      // Resizing from bottom - change end time with 30-minute snapping
      const newEndMinutes = originalEnd.getHours() * 60 + originalEnd.getMinutes() + deltaMinutes;
      const snappedEndMinutes = snapToGrid(newEndMinutes);
      
      newStartDateTime = originalStart;
      newEndDateTime = new Date(originalEnd);
      newEndDateTime.setHours(Math.floor(snappedEndMinutes / 60), snappedEndMinutes % 60, 0, 0);
    }
    
    // Minimum duration: 30 minutes
    const minDuration = 30 * 60 * 1000;
    if (newEndDateTime - newStartDateTime < minDuration) {
      return;
    }
    
    // Store current resize values in ref (like CalendarView)
    currentResizeValues.current = {
      startDateTime: newStartDateTime.toISOString(),
      endDateTime: newEndDateTime.toISOString()
    };
    
    // Update resize preview
    const userTimeZone = getUserTimeZone();
    const startTime = newStartDateTime.toLocaleTimeString(getLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimeZone
    });
    const endTime = newEndDateTime.toLocaleTimeString(getLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimeZone
    });
    
    setResizePreview({
      startTime,
      endTime,
      position: { x: e.clientX, y: e.clientY }
    });
    
    // Update the ticket temporarily for visual feedback (like CalendarView)
    const updatedTicket = {
      ...ticket,
      plannedStartDateTime: newStartDateTime.toISOString(),
      plannedEndDateTime: newEndDateTime.toISOString()
    };
    
    // Update the user tickets array for immediate visual feedback
    setUserTickets(prev => {
      const updatedUserTickets = { ...prev };
      Object.keys(updatedUserTickets).forEach(userEmail => {
        updatedUserTickets[userEmail] = updatedUserTickets[userEmail].map(t => 
          t.id === ticket.id ? updatedTicket : t
        );
      });
      return updatedUserTickets;
    });
  };

  const handleResizeEndWithTicket = async (e, ticket, handle) => {
    if (!ticket) {
      // Cleanup listeners even if no ticket is being resized
      cleanupResizeListeners();
      return;
    }
    
    // Cleanup event listeners first
    cleanupResizeListeners();
    
    setShowResizePreview(false);
    setResizePreview(null);
    
    // Set flag to prevent click event after resize
    setJustResized(true);
    
    // Reset flag after a short delay to allow normal clicks again
    setTimeout(() => {
      setJustResized(false);
      setResizeMode(false); // Deaktiviere Resize-Modus
    }, 150);
    
    // Set operation in progress for resize
    setOperationInProgress(true);
    setOperationMessage(t('global.changingTicketDuration', 'Ticket-Dauer wird geändert...'));
    
    try {
      // Check if we have valid resize values from the ref
      if (!currentResizeValues.current.startDateTime || !currentResizeValues.current.endDateTime) {
        return;
      }
      
      const newStartDateTime = new Date(currentResizeValues.current.startDateTime);
      const newEndDateTime = new Date(currentResizeValues.current.endDateTime);
      
      // Minimum duration: 30 minutes
      const minDuration = 30 * 60 * 1000;
      if (newEndDateTime - newStartDateTime < minDuration) {
        return;
      }
      
      // Update ticket planning
      const planningUpdateData = {
        startDateTime: currentResizeValues.current.startDateTime,
        endDateTime: currentResizeValues.current.endDateTime,
        isPlanned: true
      };

      await apiService.updateTicketPlanning(ticket.id, planningUpdateData);
      
      // Silent refresh to update ticket timing immediately - only reload affected user
      const affectedUser = ticket.assignedTo;
      if (affectedUser) {
        await loadSpecificUsersData([affectedUser]);
        console.log('🌍 [GLOBAL CALENDAR] Silent refresh completed for resize operation');
      } else {
        // Fallback to manual refresh if no specific user
        await handleRefresh();
      }
      
    } catch (error) {
      console.error('Error updating ticket planning during resize:', error);
      setError(t('global.changeDurationError', 'Fehler beim Ändern der Ticket-Dauer'));
      // Silent refresh to restore original state - only reload affected user
      const affectedUser = ticket.assignedTo;
      if (affectedUser) {
        await loadSpecificUsersData([affectedUser]);
        console.log('🌍 [GLOBAL CALENDAR] Silent refresh completed for error recovery');
      } else {
        // Fallback to manual refresh if no specific user
        await handleRefresh();
      }
    } finally {
      // Clean up all resize state
      setResizingTicket(null);
      setResizeHandle(null);
      setResizeStartPos({ x: 0, y: 0 });
      setResizeStartHeight(0);
      
      // Reset resize values
      currentResizeValues.current = { startDateTime: null, endDateTime: null };
      
      // Reset operation state
      setOperationInProgress(false);
      setOperationMessage('');
    }
  };

  // User filter functions
  const toggleUserSelection = (userEmail) => {
    const newSelectedUsers = new Set(selectedUsers);
    if (newSelectedUsers.has(userEmail)) {
      newSelectedUsers.delete(userEmail);
    } else {
      newSelectedUsers.add(userEmail);
    }
    setSelectedUsers(newSelectedUsers);
  };

  const selectAllUsers = () => {
    const allUserEmails = new Set(users.map(user => user.email));
    setSelectedUsers(allUserEmails);
  };

  const deselectAllUsers = () => {
    setSelectedUsers(new Set());
  };

  // Get filtered users for display
  // #308: memoisiert, weil daran die Zellenberechnung haengt -- ein neues
  // Array bei jedem Render wuerde jede Memoisierung dahinter entwerten.
  const displayedUsers = useMemo(
    () => users.filter(user => selectedUsers.has(user.email)),
    [users, selectedUsers]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showUserDropdown]);

  // Close context menu when clicking outside
  // #189: zusätzlich contextmenu (Capture) — Rechtsklick feuert kein click,
  // dadurch blieben Ticket- und Termin-Menü beim Objektart-Wechsel offen.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, ticket: null });
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('contextmenu', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside, true);
    };
  }, [contextMenu.visible]);

  // Close event context menu when clicking outside
  useEffect(() => {
    const handleClickOutsideEvent = () => {
      if (eventContextMenu.visible) {
        setEventContextMenu({ visible: false, x: 0, y: 0, event: null, userEmail: null });
      }
    };
    document.addEventListener('click', handleClickOutsideEvent);
    document.addEventListener('contextmenu', handleClickOutsideEvent, true);
    return () => {
      document.removeEventListener('click', handleClickOutsideEvent);
      document.removeEventListener('contextmenu', handleClickOutsideEvent, true);
    };
  }, [eventContextMenu.visible]);

  const isSameDay = (d1, d2) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  // Filter out Graph events that have corresponding tickets (GLOBAL version)
  // NEW: Only filter the chronologically latest duplicate, keep earlier ones
  const filterDuplicateGraphEvents = useCallback((items) => {
    const tickets = items.filter(item => item.type === 'ticket');
    const events = items.filter(item => item.type === 'event');
    
    // Get ALL tickets from ALL users for global comparison
    const allGlobalTickets = [];
    Object.values(userTickets).forEach(userTicketArray => {
      allGlobalTickets.push(...userTicketArray);
    });
    
    // Get all ticket numbers and calendar event IDs from ALL tickets (not just current user)
    const allTicketNumbers = new Set([
      ...tickets.map(item => item.data.ticketNumber),
      ...allGlobalTickets.map(ticket => ticket.ticketNumber)
    ]);
    const allCalendarEventIds = new Set([
      ...tickets.map(item => item.data.calendarEventId).filter(Boolean),
      ...allGlobalTickets.map(ticket => ticket.calendarEventId).filter(Boolean)
    ]);
    
    
    // NEW: Group duplicate events by their duplicate criteria to find the latest ones to filter
    const duplicateGroups = new Map();
    
    events.forEach(eventItem => {
      const event = eventItem.data;
      let duplicateKey = null;
      
      // Check if this event ID matches any ticket's calendarEventId (globally)
      if (allCalendarEventIds.has(event.id)) {
        duplicateKey = `calendar-id:${event.id}`;
      }
      
      // Check if event body contains a ticket reference that matches an existing ticket (globally)
      const ticketNumberFromEvent = extractTicketNumberFromEvent(event.body);
      if (ticketNumberFromEvent && allTicketNumbers.has(ticketNumberFromEvent)) {
        duplicateKey = `ticket-number:${ticketNumberFromEvent}`;
      }
      
      // If this event is a duplicate, add it to the appropriate group
      if (duplicateKey) {
        if (!duplicateGroups.has(duplicateKey)) {
          duplicateGroups.set(duplicateKey, []);
        }
        duplicateGroups.get(duplicateKey).push(eventItem);
      }
    });
    
    // Determine which events to filter out (only the latest in each duplicate group)
    const eventsToFilter = new Set();
    
    duplicateGroups.forEach((duplicateEvents, key) => {
      if (duplicateEvents.length > 1) {
        // Sort by start time (latest first)
        const sortedDuplicates = duplicateEvents.sort((a, b) => {
          const timeA = getItemTimeRange(a);
          const timeB = getItemTimeRange(b);
          return timeB.start - timeA.start; // Latest first
        });
        
        // Only filter out the latest (first in sorted array), keep the earlier ones
        const latestEvent = sortedDuplicates[0];
        eventsToFilter.add(latestEvent.data.id);
        
      } else if (duplicateEvents.length === 1) {
        // Single duplicate, filter it out as before
        eventsToFilter.add(duplicateEvents[0].data.id);
        
      }
    });
    
    // Filter out only the events marked for filtering
    const filteredEvents = events.filter(eventItem => {
      const event = eventItem.data;
      
      if (eventsToFilter.has(event.id)) {
        return false;
      }
      
      return true;
    });
    
    
    // Return all tickets plus filtered events
    return [...tickets, ...filteredEvents];
  }, [userTickets, extractTicketNumberFromEvent, getItemTimeRange]);

  // #308: Eine Zelle des Rasters (Bearbeiter x Tag) auf einen Schlag.
  //
  // Vorher lag das in zwei Haelften: getUserEventsForDay lieferte die
  // positionierten Eintraege, und die Render-Schleife filterte den Tag GLEICH
  // NOCH EINMAL, um "gibt es Termine oberhalb/unterhalb des Fensters" zu
  // beantworten. Gemessen war diese zweite Filterung mit 15,2 ms je Render
  // teurer als alles, was die erste tat (10,8 ms) -- und jede Zelle legte sich
  // dafuer ein eigenes Intl.DateTimeFormat an (zusammen 8,1 ms).
  //
  // hasAbove/hasBelow werden bewusst aus den UNGEFILTERTEN Tageslisten
  // bestimmt, genau wie vorher: ein Termin ausserhalb des Fensters soll den
  // Pfeil auch dann zeigen, wenn er als Ticket-Dublette aussortiert wuerde.
  const berechneZelle = useCallback((userEmail, day, hourFmt) => {
    const { dayStart, dayEnd } = tagesGrenzen(day);
    const allItems = tagesEintraege(userEvents[userEmail], userTickets[userEmail], dayStart, dayEnd);

    return {
      // Calculate positions for overlapping items
      items: calculateItemPositions(filterDuplicateGraphEvents(allItems)),
      ...ausserhalbFenster(allItems, hourFmt)
    };
  }, [userEvents, userTickets, tagesGrenzen, tagesEintraege, ausserhalbFenster,
      calculateItemPositions, filterDuplicateGraphEvents]);

  // #308: memoisiert -- die IIFE legte bei JEDEM Render neue Date-Objekte an.
  const visibleDays = useMemo(() => {
    if (viewMode === 'day') {
      const d = new Date(currentDate);
      d.setHours(0, 0, 0, 0);
      return [d];
    }
    // Compute start of current week (Mon-Sun)
    const today = new Date(currentDate);
    const day = today.getDay(); // 0=Sun,1=Mon...
    const diffToMonday = (day === 0 ? -6 : 1 - day);
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() + diffToMonday);
    weekStart.setHours(0, 0, 0, 0);

    const totalDays = showWeekend ? 7 : 5;
    const days = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return days;
  }, [viewMode, currentDate, showWeekend]);

  // #308: Das Raster wird einmal je Datenlage gerechnet, nicht einmal je
  // Render. Bei 9 Bearbeitern x 5 Tagen sind das 45 Zellen; die Datei haelt
  // 63 useState -- bisher loeste jede einzelne Zustandsaenderung (Ziehen,
  // Groesse aendern, ein geoeffnetes Menue) die komplette Rechnung erneut aus.
  // Gemessen: 32 ms je Render, bei 60 Bildern/s stehen 16,7 ms fuer ALLES zur
  // Verfuegung.
  //
  // Das Intl.DateTimeFormat entsteht hier EINMAL statt 45-mal -- es war mit
  // 8,1 ms je Render ein Viertel der Gesamtkosten.
  const zellenDaten = useMemo(() => {
    const karte = new Map();
    const hourFmt = erstelleStundenFormat();
    displayedUsers.forEach((user) => {
      visibleDays.forEach((day) => {
        karte.set(`${user.email}|${day.getTime()}`, berechneZelle(user.email, day, hourFmt));
      });
    });
    return karte;
  }, [displayedUsers, visibleDays, berechneZelle, erstelleStundenFormat]);

  const renderCalendarItem = (item, index, userEmail) => {
    const itemPosition = item.position || {
      column: 0,
      totalColumns: 1,
      left: 2,
      width: 'calc(100% - 4px)'
    };

    if (item.type === 'event') {
      const event = item.data;
      const status = getEventStatus(event.showAs);
      const position = getEventPosition(event);
      const height = getEventHeight(event);
      const isSearchMatched = matchedEventIds.includes(event.id);
      const isCurrent = currentMatch && currentMatch.type === 'event' && currentMatch.id === event.id;
      let topPercent = position;
      try {
        const startUtcDateTime = event.start.dateTime.endsWith('Z') ? event.start.dateTime : event.start.dateTime + 'Z';
        const start = new Date(startUtcDateTime);
        const userTimeZone = getUserTimeZone();
        const timeFormat = new Intl.DateTimeFormat(getLocale(), { timeZone: userTimeZone, hour: 'numeric', hour12: false });
        const startHour = parseInt(timeFormat.format(start));
        if (startHour >= timeEndHour) {
          topPercent = Math.max(0, 100 - height);
        } else if (startHour < timeStartHour) {
          topPercent = 0;
        }
      } catch (e) {}
      
      return (
        <div
          key={`event-${userEmail}-${index}`}
          className="global-calendar-event global-calendar-event-positioned calendar-event calendar-event-positioned"
          style={{
            position: 'absolute',
            top: `${topPercent}%`,
            left: itemPosition.left,
            width: itemPosition.width,
            height: `${height}%`,
            backgroundColor: isSearchMatched ? 'var(--color-search-hit)' : status.color,
            color: 'white',
            padding: '2px 4px',
            borderRadius: '3px',
            fontSize: '10px',
            lineHeight: '1.1',
            cursor: 'pointer',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 10,
            border: isCurrent ? '3px solid var(--color-search-hit)' : undefined,
            boxShadow: isCurrent ? '0 0 0 3px var(--color-search-hit-glow-60), 0 0 12px var(--color-search-hit-glow-90)' : undefined
          }}
          data-item-id={`global-event-${event.id}`}
          title={`${event.subject}\n${formatTime(event.start.dateTime)} - ${formatTime(event.end.dateTime)}\n${event.location?.displayName || ''}`}
          onClick={(e) => handleEventClick(e, event)}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setEventContextMenu({ visible: true, x: e.clientX, y: e.clientY, event, userEmail });
          }}
        >
          <div style={{ fontWeight: 'bold', fontSize: '10px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {event.subject || t('global.noSubject', '(Kein Betreff)')}
          </div>
          <div style={{ fontSize: '8px', opacity: 0.9 }}>
            {formatTime(event.start.dateTime)}
          </div>
          {event.location?.displayName && height > 15 && (
            <div style={{ fontSize: '8px', opacity: 0.8, marginTop: '1px' }}>
              <MapPin size={6} style={{ marginRight: '1px', verticalAlign: 'middle' }} />
              {event.location.displayName.substring(0, 15)}
            </div>
          )}
          {(() => {
            try {
              const provider = String(event?.onlineMeetingProvider || '').toLowerCase();
              const joinUrl = event?.onlineMeeting?.joinUrl || event?.onlineMeetingUrl || null;
              const isTeams = Boolean((event?.isOnlineMeeting && provider.includes('teams')) || (joinUrl && /https?:\/\/(.*\.)?teams\.microsoft\.com/i.test(joinUrl)));
              if (!isTeams) return null;
              return (
                <div
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1px',
                    zIndex: 30,
                    pointerEvents: 'none',
                    backgroundColor: 'var(--color-glass-90)',
                    borderRadius: '3px',
                    boxShadow: '0 1px 3px var(--color-shadow-30)'
                  }}
                  title={t('eventDetail.teamsMarker', 'Teams-Besprechung')}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      lineHeight: '12px',
                      display: 'block',
                      textAlign: 'center'
                    }}
                  >
                    🎥
                  </span>
                </div>
              );
            } catch (_) { return null; }
          })()}
        </div>
      );
    } else {
      const ticket = item.data;
      const color = ticket.isArchived ? getEventStatus('busy').color : getTicketColor(ticket);
      const position = getTicketPosition(ticket);
      const height = getTicketHeight(ticket);
      let topPercent = position;
      try {
        const start = new Date(ticket.plannedStartDateTime);
        const userTimeZone = getUserTimeZone();
        const timeFormat = new Intl.DateTimeFormat(getLocale(), { timeZone: userTimeZone, hour: 'numeric', hour12: false });
        const startHour = parseInt(timeFormat.format(start));
        if (startHour >= timeEndHour) {
          topPercent = Math.max(0, 100 - height);
        } else if (startHour < timeStartHour) {
          topPercent = 0;
        }
      } catch (e) {}
      const isDragging = draggedPlannedTicket?.id === ticket.id;
      
      
      // Neon-Highlight, wenn URL-Highlight aktiv, Ticket-ID und User übereinstimmen
      const isHighlighted = !!(highlightMode && highlightTicketId === ticket.id && (!highlightUserEmail || highlightUserEmail === userEmail));
      const isSearchMatched = matchedTicketIds.includes(ticket.id);
      const isCurrent = currentMatch && currentMatch.type === 'ticket' && currentMatch.id === ticket.id;

      return (
        <div
          key={`ticket-${userEmail}-${index}`}
          className={`global-calendar-ticket global-calendar-ticket-positioned calendar-ticket ${isDragging ? 'dragging' : ''} ${operationInProgress ? 'operation-blocked' : ''} ${hatKennung(ticket) ? 'has-markers' : ''} ${ticket.isArchived ? 'archived-ticket' : ''}`}
          style={{
            position: 'absolute',
            top: `${topPercent}%`,
            left: itemPosition.left,
            width: itemPosition.width,
            height: `${height}%`,
            backgroundColor: (isSearchMatched || isHighlighted) ? 'var(--color-search-hit)' : color,
            color: 'white',
            padding: '2px 4px',
            paddingRight: kennungPlatz(ticket), // #604: Platz für Marker, Teams und Prioritätspunkt
            borderRadius: '3px',
            fontSize: '10px',
            lineHeight: '1.1',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            border: (isCurrent || isHighlighted) ? '3px solid var(--color-search-hit)' : '2px solid var(--color-glass-50)',
            boxShadow: (isCurrent || isHighlighted) ? '0 0 0 3px var(--color-search-hit-glow-60), 0 0 12px var(--color-search-hit-glow-90)' : undefined,
            zIndex: isDragging ? 15 : 10,
            opacity: isDragging ? 0.5 : 1,
            cursor: 'pointer'
          }}
          data-item-id={`global-ticket-${ticket.id}`}
          title={`${ticket.title}\nTicket: ${ticket.ticketNumber}\n${formatTicketTime(ticket.plannedStartDateTime)} - ${formatTicketTime(ticket.plannedEndDateTime)}\nPriorität: ${ticket.priority}\n\nTipp: Ziehen Sie an den Rändern, um die Dauer zu ändern`}
          draggable={!resizeMode && !operationInProgress && !ticket.isArchived}
          onDragStart={(e) => { if (ticket.isArchived) { e.preventDefault(); return; } handlePlannedTicketDragStart(e, ticket); }}
          onDragEnd={handlePlannedTicketDragEnd}
          onClick={(e) => handleTicketClick(e, ticket)}
          onContextMenu={(e) => { if (ticket.isArchived) { e.preventDefault(); e.stopPropagation(); return; } handleTicketRightClick(e, ticket); }}
        >
          {/* #604: Kopfzeile -- Marker, Teams, Prioritätspunkt ganz rechts */}
          <KalenderTicketKennung ticket={ticket} />
          
          {/* Resize handle top */}
          <div
            className="resize-handle resize-handle-top"
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '5px',
              cursor: 'n-resize',
              backgroundColor: 'var(--color-glass-10)',
              zIndex: 30,
              borderTop: '1px solid var(--color-glass-30)',
              transition: 'all 0.2s ease'
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              if (ticket.isArchived) return;
              handleResizeStart(e, ticket, 'top');
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-glass-90)';
              e.target.style.borderTop = '3px solid var(--color-border-inverse)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-glass-10)';
              e.target.style.borderTop = '1px solid var(--color-glass-30)';
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
            }}
            onDragStart={(e) => e.preventDefault()}
            draggable={false}
          />
          
          {(ticket.hasNewActivity || ticket.hasNewEmails) && (
            <div 
              className="new-activity-indicator" 
              style={{
                position: 'absolute',
                top: '0',
                right: '0',
                width: '0',
                height: '0',
                borderLeft: '15px solid transparent',
                borderTop: '15px solid var(--color-danger)',
                zIndex: 1
              }}
              title={t('common.newActivity', 'Neue Aktivität')}
            />
          )}
          
          <CalendarTicketTitle
            ticket={ticket}
            titleStyle={{
              fontWeight: 'bold',
              fontSize: '10px',
              marginTop: '4px',
              marginBottom: '2px',
              pointerEvents: 'none',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere'
            }}
          />
          <div style={{ 
            fontSize: '8px', 
            opacity: 0.9,
            pointerEvents: 'none'
          }}>
            {formatTicketTime(ticket.plannedStartDateTime)}
          </div>
          {height > 15 && (
            <div style={{ 
              fontSize: '8px', 
              opacity: 0.8, 
              marginTop: '1px',
              marginBottom: '4px',
              pointerEvents: 'none'
            }}>
              #{ticket.ticketNumber}
            </div>
          )}
          
          {/* Resize handle bottom */}
          <div
            className="resize-handle resize-handle-bottom"
            style={{
              position: 'absolute',
              bottom: '0',
              left: '0',
              right: '0',
              height: '5px',
              cursor: 's-resize',
              backgroundColor: 'var(--color-glass-10)',
              zIndex: 30,
              borderBottom: '1px solid var(--color-glass-30)',
              transition: 'all 0.2s ease'
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              if (ticket.isArchived) return;
              handleResizeStart(e, ticket, 'bottom');
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-glass-90)';
              e.target.style.borderBottom = '3px solid var(--color-border-inverse)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-glass-10)';
              e.target.style.borderBottom = '1px solid var(--color-glass-30)';
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
            }}
            onDragStart={(e) => e.preventDefault()}
            draggable={false}
          />
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <RefreshCw className="animate-spin" size={32} />
          <p>{t('global.loading', 'Lade Kalender...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const timeSlots = getTimeSlots();

  // Build sidebar assignee filter options (Projektmodus: nur relevante Benutzer, keine "Offene")
  const sidebarAssigneeOptions = (() => {
    const opts = [];
    const baseUsers = projectPlanId && projectRelevantUserEmails.size > 0
      ? (users || []).filter(u => projectRelevantUserEmails.has(u.email))
      : displayedUsers || [];
    if (!projectPlanId) {
      opts.push({ type: 'unassigned', label: t('global.openShort', 'Pool') });
    }
    baseUsers.forEach(user => {
      let lastNameLabel = '';
      if (user.lastName && user.lastName.trim() !== '') {
        lastNameLabel = user.lastName.trim();
      } else if (user.displayName && user.displayName.trim() !== '') {
        const parts = user.displayName.trim().split(/\s+/);
        lastNameLabel = parts[parts.length - 1];
      } else if (user.firstName || user.lastName) {
        const combined = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        const parts = combined.split(/\s+/);
        lastNameLabel = parts[parts.length - 1] || combined;
      } else if (user.email) {
        lastNameLabel = (user.email.split('@')[0]) || user.email;
      } else {
        lastNameLabel = 'Benutzer';
      }
      const label = lastNameLabel;
      opts.push({ type: 'user', email: user.email, label });
    });
    return opts;
  })();

  return (
    <div className="container">
      <div className="global-calendar-with-sidebar">
        <TicketSidebar 
          isLoading={loading}
          onRefresh={handleSidebarOnlyRefresh}
          hideViewToggle={true}
          assigneeFilterOptions={sidebarAssigneeOptions}
          projectPlanningFilter={projectPlanId ? { projectId: Number(projectPlanId), relatedSubIds: projectRelatedSubIds } : null}
          onTicketDragStart={(ticket) => {
            // Block sidebar ticket drag start if operation is in progress
            if (operationInProgress) {
              console.log('🚫 Sidebar drag blocked - operation in progress:', operationMessage);
              return false; // Return false to signal blocking
            }
            setDraggedTicket(ticket);
            return true; // Return true to signal success
          }}
          onTicketDragEnd={() => {
            setDraggedTicket(null);
            setShowDragPreview(false);
          }}
          onCreateTicket={handleCreateTicketClick}
          onTicketClick={handleTicketClick}
          onTicketPlanned={handleTicketPlannedCallback}
        />
        
        <div className="global-calendar-main">
          <div className="global-calendar-view">
            <div className="global-calendar-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ flex: '0 0 auto' }}>
                <Calendar size={24} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
                {viewMode === 'day' ? (
                  currentDate.toLocaleDateString(getLocale(), { weekday: 'short', day: '2-digit', month: '2-digit' })
                ) : (
                  (() => {
                    const d = new Date(currentDate);
                    const dn = d.getDay() || 7; // 1..7
                    d.setDate(d.getDate() + 4 - dn);
                    const yearStart = new Date(Date.UTC(d.getFullYear(), 0, 1));
                    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
                    return `KW${weekNo}`;
                  })()
                )}
              </h2>
              <div className="global-calendar-actions" style={{ flex: '1 1 auto', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <div className="sidebar-search-container" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div className="search-input-wrapper">
                    <Search size={16} className="search-icon" />
                    <input
                      type="text"
                      placeholder={t('global.searchInCalendar', 'Im Kalender suchen...')}
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); performSearch(e.target.value); }}
                      className="sidebar-search-input"
                    />
                  </div>
                  <button
                    onClick={goToPrevSearchResult}
                    className="btn btn-secondary icon-only"
                    disabled={!searchResults || searchResults.length === 0 || currentSearchIndex <= 0}
                    title={t('global.prevResult', 'Vorheriges Ergebnis')} aria-label={t('global.prevResult', 'Vorheriges Ergebnis')}
                    style={{ padding: '2px', minWidth: 'auto', width: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    onClick={goToNextSearchResult}
                    className="btn btn-secondary icon-only"
                    disabled={!searchResults || searchResults.length === 0 || currentSearchIndex >= searchResults.length - 1}
                    title={t('global.nextResult', 'Nächstes Ergebnis')} aria-label={t('global.nextResult', 'Nächstes Ergebnis')}
                    style={{ padding: '2px', minWidth: 'auto', width: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
                <button 
                  onClick={() => navigateDay(-1)}
                  className="btn btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <ChevronLeft size={16} />
                  {t('calendar.previousWeek', 'Vorherige Woche')}
                </button>
                
                <button 
                  onClick={goToToday}
                  className="btn btn-primary"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  disabled={isToday(currentDate)}
                >
                  <Target size={16} />
                  {t('calendar.today', 'Heute')}
                </button>
                
                <button 
                  onClick={() => navigateDay(1)}
                  className="btn btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  {t('calendar.nextWeek', 'Nächste Woche')}
                  <ChevronRight size={16} />
                </button>
                
                <div className="user-dropdown-container" style={{ position: 'relative' }}>
                  <button 
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                    className="btn btn-primary"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <Users size={16} />
                    {t('global.users', 'Benutzer')} ({selectedUsers.size})
                    <ChevronDown size={16} />
                  </button>
                  
                  {showUserDropdown && (
                    <div className="user-dropdown" style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      background: 'white',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px var(--color-shadow-15)',
                      zIndex: 1000,
                      minWidth: '250px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      marginTop: '5px'
                    }}>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-faint)' }}>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <button 
                            onClick={selectAllUsers}
                            className="btn btn-sm btn-outline"
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            {t('header.allCalendars', 'Alle Kalender').split(' ')[0]}
                          </button>
                          <button 
                            onClick={deselectAllUsers}
                            className="btn btn-sm btn-outline"
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            {t('common.none', 'Keine')}
                          </button>
                        </div>
                      </div>
                      
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {users.map(user => (
                          <label 
                            key={user.email}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--color-divider-faint)'
                            }}
                            onClick={() => toggleUserSelection(user.email)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(user.email)}
                              onChange={() => toggleUserSelection(user.email)}
                              style={{ marginRight: '12px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '500', fontSize: '14px' }}>
                                {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                                {user.email}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="week-toggle-container" style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleToggleViewMode}
                    className="btn btn-secondary"
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    title={getViewModeTooltip(getNextViewMode())}
                  >
                    <Calendar size={16} />
                    {getViewModeLabel(getNextViewMode())}
                  </button>
                </div>
              </div>
            </div>

            {displayedUsers.length === 0 ? (
              <div className="global-calendar-empty">
                <Users size={48} style={{ opacity: 0.3 }} />
                <p>{t('global.noUsersFound', 'Keine Benutzer gefunden')}</p>
                <p style={{ fontSize: '14px', color: 'var(--color-text-placeholder)', marginTop: '10px' }}>
                  {t('global.checkConsoleOrContactAdmin', 'Bitte prüfen Sie die Browser-Konsole für Details oder wenden Sie sich an den Administrator.')}
                </p>
              </div>
            ) : (
              <div className="global-calendar-grid">
                {/* User group header row: username spans the group's days */}
                <div
                  className="global-calendar-user-header"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `var(--time-col-width) repeat(${displayedUsers.length * visibleDays.length}, 1fr)`,
                    gap: '1px',
                    marginBottom: '1px'
                  }}
                >
                  <div className="time-column-header"></div>
                  {displayedUsers.map((user, uIdx) => (
                    <div
                      key={`user-group-${uIdx}`}
                      className={`user-group-header ${uIdx % 2 === 0 ? 'group-even' : 'group-odd'} user-group-start user-group-end`}
                      style={{ gridColumn: `span ${visibleDays.length}` }}
                      title={user.email}
                    >
                      {user.lastName || user.displayName || user.email.split('@')[0]}
                    </div>
                  ))}
                </div>

                {/* Day header row per user group */}
                <div
                  className="global-calendar-day-header"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `var(--time-col-width) repeat(${displayedUsers.length * visibleDays.length}, 1fr)`,
                    gap: '1px',
                    marginBottom: '1px'
                  }}
                >
                  <div className="time-column-header">{t('global.time', 'Zeit')}</div>
                  {displayedUsers.map((user, uIdx) => (
                    visibleDays.map((day, dIdx) => (
                      <div
                        key={`day-hdr-${uIdx}-${dIdx}`}
                        className={`day-header ${isSameDay(day, new Date()) ? 'today' : ''} ${dIdx === 0 ? 'user-group-start' : ''} ${dIdx === visibleDays.length - 1 ? 'user-group-end' : ''} ${uIdx % 2 === 0 ? 'group-even' : 'group-odd'}`}
                        title={day.toLocaleDateString(getLocale())}
                      >
                        <div className="day-name" style={{ fontWeight: isSameDay(day, new Date()) ? '700' : '600', color: isSameDay(day, new Date()) ? 'var(--color-primary)' : undefined }}>
                          {day.toLocaleDateString(getLocale(), { weekday: 'short', timeZone: getUserTimeZone() })}
                        </div>
                        <div className="day-date" style={{ fontWeight: isSameDay(day, new Date()) ? '700' : '500', color: isSameDay(day, new Date()) ? 'var(--color-primary)' : undefined }}>
                          {day.toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', timeZone: getUserTimeZone() })}
                        </div>
                      </div>
                    ))
                  ))}
                </div>

                {/* Calendar Body */}
                <div
                  className="global-calendar-week-body"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `var(--time-col-width) repeat(${displayedUsers.length * visibleDays.length}, 1fr)`,
                    gap: '1px'
                  }}
                >
                  {/* Time Column */}
                  <div className="time-column" style={{ width: 'var(--time-col-width)' }}>
                    {timeSlots.map(hour => (
                      <div key={hour} className="time-slot" style={{ height: `${100 / (timeEndHour - timeStartHour)}%` }}>
                        {hour.toString().padStart(2, '0')}:00
                      </div>
                    ))}
                  </div>

                  {/* User Columns per Day */}
                  {displayedUsers.map((user, uIdx) => (
                    visibleDays.map((day, dIdx) => {
                      const { items: userItems, hasAbove, hasBelow } =
                        zellenDaten.get(`${user.email}|${day.getTime()}`) || LEERE_ZELLE;
                      const isDragOver = !!(dragOverCell && dragOverCell.userEmail === user.email && isSameDay(dragOverCell.day, day));
                      const groupStart = dIdx === 0 ? 'user-group-start' : '';
                      const groupEnd = dIdx === visibleDays.length - 1 ? 'user-group-end' : '';
                      const groupBg = uIdx % 2 === 0 ? 'group-even' : 'group-odd';
                      return (
                        <div
                          key={`col-${uIdx}-${dIdx}-${user.email}`}
                          className={`user-column drop-zone ${groupStart} ${groupEnd} ${groupBg} ${isDragOver ? 'drag-over' : ''} ${operationInProgress ? 'operation-blocked' : ''}`}
                          style={{ position: 'relative' }}
                          onDragOver={(e) => handleDragOver(e, user.email, day)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDropOnUser(e, user.email, day)}
                          title={operationInProgress ? `${t('global.pleaseWait', 'Warten Sie:')} ${operationMessage}` : ''}
                        >
                          {/* Above indicator */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (timeStartHour <= 1) {
                                setTimeStartHour(8);
                              } else if (hasAbove) {
                                setTimeStartHour(0);
                              } else {
                                setTimeStartHour(1);
                              }
                            }}
                            className="btn btn-xxs"
                            title={hasAbove ? t('global.showEarlyAppointments', 'Frühe Termine anzeigen') : t('global.expandTo1am', 'Bis 1 Uhr erweitern')}
                            style={{
                              padding: '0 6px',
                              fontSize: '10px',
                              lineHeight: '12px',
                              border: 'none',
                              background: 'transparent',
                              color: hasAbove ? 'var(--color-danger)' : 'var(--color-text-faint)',
                              cursor: 'pointer',
                              position: 'absolute',
                              top: 0,
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              zIndex: 900,
                              pointerEvents: 'auto'
                            }}
                          >
                            ▲
                          </button>
                          <div className="user-day-section" onMouseDown={(e) => handleSelectionStart(e, user.email, day)} style={{ position: 'relative' }}>
                            <div className="user-day-events">
                              {userItems.map((item, index) => renderCalendarItem(item, index, user.email))}
                              {/* Ghost-Vorschau für neue Tickets aus der Sidebar */}
                              {draggedTicket && isDragOver && showDragPreview && dragPreview && typeof dragPreview.topPercent === 'number' && (
                                <div
                                  className="global-calendar-ticket global-calendar-ticket-positioned calendar-ticket ghost"
                                  style={{
                                    position: 'absolute',
                                    top: `${dragPreview.topPercent}%`,
                                    left: 2,
                                    width: 'calc(100% - 4px)',
                                    height: `${dragPreview.heightPercent}%`,
                                    backgroundColor: getTicketColor(draggedTicket),
                                    color: 'white',
                                    padding: '2px 4px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    lineHeight: '1.1',
                                    border: '2px dashed var(--color-glass-90)',
                                    opacity: 0.7,
                                    zIndex: 14,
                                    pointerEvents: 'none',
                                    display: 'flex',
                                    flexDirection: 'column'
                                  }}
                                  title={`${draggedTicket.title}\n${dragPreview.startTime}`}
                                >
                                  <div style={{ fontWeight: 'bold', fontSize: '10px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                    {draggedTicket.title}
                                  </div>
                                  <div style={{ fontSize: '8px', opacity: 0.9 }}>{dragPreview.startTime}</div>
                                </div>
                              )}
                              {draggedPlannedTicket && isDragOver && showDragPreview && dragPreview && typeof dragPreview.topPercent === 'number' && (
                                <div
                                  className="global-calendar-ticket global-calendar-ticket-positioned calendar-ticket ghost"
                                  style={{
                                    position: 'absolute',
                                    top: `${dragPreview.topPercent}%`,
                                    left: 2,
                                    width: 'calc(100% - 4px)',
                                    height: `${dragPreview.heightPercent}%`,
                                    backgroundColor: getTicketColor(draggedPlannedTicket),
                                    color: 'white',
                                    padding: '2px 4px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    lineHeight: '1.1',
                                    border: '2px dashed var(--color-glass-90)',
                                    opacity: 0.7,
                                    zIndex: 14,
                                    pointerEvents: 'none',
                                    display: 'flex',
                                    flexDirection: 'column'
                                  }}
                                  title={`${draggedPlannedTicket.title}\n${dragPreview.startTime}`}
                                >
                                  <div style={{ fontWeight: 'bold', fontSize: '10px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                    {draggedPlannedTicket.title}
                                  </div>
                                  <div style={{ fontSize: '8px', opacity: 0.9 }}>{dragPreview.startTime}</div>
                                </div>
                              )}
                            </div>
                            {/* Selection overlay */}
                            {isSelecting && selectionUserEmail === user.email && selectionDay && day.toDateString() === new Date(selectionDay).toDateString() && selectionRect && (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: '2px',
                                  right: '2px',
                                  top: `${selectionRect.topPercent}%`,
                                  height: `${selectionRect.heightPercent}%`,
                                  backgroundColor: 'var(--color-cal-auswahl)',
                                  border: '2px dashed var(--color-cal-auswahl-rand)',
                                  borderRadius: '4px',
                                  pointerEvents: 'none',
                                  zIndex: 20
                                }}
                              />
                            )}

                            {/* Current time marker */}
                            {isToday(day) && getCurrentTimePosition(currentTime) !== null && (
                              <div
                                className="current-time-marker"
                                style={{
                                  position: 'absolute',
                                  top: `${getCurrentTimePosition(currentTime)}%`,
                                  left: '0',
                                  right: '0',
                                  height: '2px',
                                  backgroundColor: 'var(--color-danger)',
                                  zIndex: 1000,
                                  boxShadow: '0 1px 3px var(--color-danger-shadow-30)',
                                  borderRadius: '1px'
                                }}
                              >
                                {/* Time indicator dot */}
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: '-6px',
                                    top: '-4px',
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: 'var(--color-danger)',
                                    borderRadius: '50%',
                                    border: '2px solid white',
                                    boxShadow: '0 2px 4px var(--color-shadow-20)'
                                  }}
                                />
                                {/* Current time label - only on first visible column */}
                                {uIdx === 0 && dIdx === 0 && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      left: '8px',
                                      top: '-12px',
                                      backgroundColor: 'var(--color-danger)',
                                      color: 'white',
                                      padding: '2px 6px',
                                      borderRadius: '3px',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      boxShadow: '0 2px 4px var(--color-shadow-20)',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {currentTime.toLocaleTimeString(getLocale(), {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZone: getUserTimeZone()
                                    })}
                                  </div>
                                )}
                              </div>
                            )}

                          {/* Time grid background */}
                          {timeSlots.map(hour => (
                            <div key={hour} className="time-slot-bg" style={{ height: `${100 / (timeEndHour - timeStartHour)}%` }}></div>
                          ))}
                          </div>
                          {/* Below indicator */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (timeEndHour >= 23) {
                                setTimeEndHour(18);
                              } else if (hasBelow) {
                                setTimeEndHour(24);
                              } else {
                                setTimeEndHour(23);
                              }
                            }}
                            className="btn btn-xxs"
                            title={hasBelow ? t('global.showLateAppointments', 'Späte Termine anzeigen') : t('global.expandTo11pm', 'Bis 23 Uhr erweitern')}
                            style={{
                              padding: '0 6px',
                              fontSize: '10px',
                              lineHeight: '12px',
                              border: 'none',
                              background: 'transparent',
                              color: hasBelow ? 'var(--color-danger)' : 'var(--color-text-faint)',
                              cursor: 'pointer',
                              position: 'absolute',
                              bottom: 0,
                              left: '50%',
                              transform: 'translate(-50%, 50%)',
                              zIndex: 900,
                              pointerEvents: 'auto'
                            }}
                          >
                            ▼
                          </button>
                        </div>
                      );
                    })
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* TICKET DETAIL MODAL */}
      {showTicketDetail && selectedTicket && (
        <TicketDetail 
          isMobileView={isMobileScreen}
          ticket={selectedTicket}
          onClose={handleTicketDetailClose}
          onTicketUpdate={handleTicketUpdate}
          hideCalendarSidebar={true}
        />
      )}

      {/* EVENT DETAIL MODAL */}
      {showEventDetail && selectedEvent && (
        <CalendarEventDetail 
          event={selectedEvent}
          onClose={() => { setShowEventDetail(false); setSelectedEvent(null); }}
        />
      )}

      {/* CREATE TICKET MODAL */}
      {showCreateTicketForm && (
        <Modal
          open
          onClose={closeCreateTicketModal}
          title={<><Plus size={24} /><h3>{t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}</h3><CalenderMarkerHeaderSelect value={newTicket.calenderMarker} onChange={(value) => setNewTicket({...newTicket, calenderMarker: value })} /></>}
          ariaLabel={t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}
          footer={<>
            <button type="button" onClick={() => setShowCreateTicketForm(false)} className="btn btn-secondary">{t('dashboard.cancel', 'Abbrechen')}</button>
            <button type="submit" form="global-calendar-create-ticket-form" className="btn btn-primary" disabled={isCreatingTicket}><Plus size={16} />{isCreatingTicket ? t('common.creating', 'Erstelle...') : t('dashboard.createTicket', 'Ticket erstellen')}</button>
          </>}
        >
            
            <form id="global-calendar-create-ticket-form" onSubmit={handleCreateTicket} className="create-ticket-form">
              <div className="form-body">
                {/* #104: Kunde (halbbreit) über dem Titel */}
                <TicketCustomerField
                  value={newTicket.customerId}
                  onChange={(customerId) => setNewTicket(prev => ({...prev, customerId}))}
                  contactValue={newTicket.contactId}
                  onContactChange={(contactId) => setNewTicket(prev => ({...prev, contactId}))}
                  inputId="ticket-customer"
                />
                <div className="form-group">
                  <label htmlFor="ticket-title">{t('dashboard.field.title', 'Titel')} <span className="required">*</span></label>
                  <input
                    id="ticket-title"
                    type="text"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                    required
                    placeholder={t('dashboard.placeholder.title', 'Ticket-Titel eingeben...')}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="ticket-description">{t('dashboard.field.description', 'Beschreibung')}</label>
                  <textarea
                    id="ticket-description"
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                    placeholder={t('dashboard.placeholder.description', 'Ticket-Beschreibung eingeben...')}
                    rows="4"
                    className="form-textarea"
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="ticket-assigned">{t('dashboard.field.assignedTo', 'Zugewiesen an')}</label>
                    <select
                      id="ticket-assigned"
                      value={newTicket.assignedTo || (pendingPlannedTimeRange?.userEmail || '')}
                      onChange={(e) => setNewTicket({ ...newTicket, assignedTo: e.target.value })}
                      className="form-select"
                    >
                      <option value="">{t('dashboard.unassigned', 'Nicht zugewiesen')}</option>
                      {availableEmails.map(email => (
                        <option key={email} value={email}>{email}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="ticket-priority">{t('dashboard.field.priority', 'Priorität')}</label>
                    <select
                      id="ticket-priority"
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                      className="form-select"
                    >
                      <option value="niedrig">Niedrig</option>
                      <option value="normal">Normal</option>
                      <option value="hoch">Hoch</option>
                      <option value="kritisch">Kritisch</option>
                    </select>
                  </div>
                  
                  
                </div>
                

              </div>
            </form>
        </Modal>
      )}
      
      {/* LIVE DRAG PREVIEW entfernt */}
      
      {/* LIVE RESIZE PREVIEW */}
      {showResizePreview && resizePreview && (
        <div 
          className="resize-preview"
          style={{
            position: 'fixed',
            left: resizePreview.position.x + 10,
            top: resizePreview.position.y - 40,
            zIndex: 9999,
            background: 'var(--color-cal-resize-hinweis)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px var(--color-shadow-30)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          ⏰ {resizePreview.startTime} - {resizePreview.endTime}
        </div>
      )}
      
      {/* OPERATION IN PROGRESS OVERLAY */}
      {operationInProgress && (
        <div 
          className="operation-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'var(--color-backdrop-10)',
            zIndex: 5000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none'
          }}
        >
          <div 
            style={{
              background: 'var(--color-cal-drag-hinweis)',
              color: 'white',
              padding: '16px 24px',
              borderRadius: '8px',
              boxShadow: '0 4px 20px var(--color-shadow-30)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            <RefreshCw className="animate-spin" size={20} />
            {operationMessage}
          </div>
        </div>
      )}

      {/* CONTEXT MENU */}
      {contextMenu.visible && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 10000,
            background: 'white',
            border: '1px solid var(--color-border-strong)',
            borderRadius: '4px',
            boxShadow: '0 4px 12px var(--color-shadow-20)',
            padding: '8px 0',
            minWidth: '180px',
            fontSize: '14px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="context-menu-item"
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              color: 'var(--color-primary)',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onClick={handleMoveToNextWeek}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span>📅</span>
            {t('contextMenu.moveNextWeek', 'Auf nächste Woche verschieben')}
          </div>
          {/* #604 */}
          <GebuchtMenuePunkt ticket={contextMenu.ticket} onClick={() => { const tk = contextMenu.ticket; setContextMenu({ visible: false, x: 0, y: 0, ticket: null }); gebuchtUmschalten(tk); }} />
          <div 
            className="context-menu-item"
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              color: 'var(--color-danger)',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onClick={handleRemoveFromPlanning}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-bg)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <span>🗑️</span>
            {t('global.removeFromPlanning', 'Aus Planung entfernen')}
          </div>
          <div style={{ 
            padding: '4px 16px', 
            fontSize: '12px', 
            color: 'var(--color-text-muted)',
            borderTop: '1px solid var(--color-border-faint)',
            marginTop: '4px'
          }}>
            {t('global.ticketLabel', 'Ticket:')} {contextMenu.ticket?.ticketNumber}
          </div>
        </div>
      )}

      {eventContextMenu.visible && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            left: eventContextMenu.x,
            top: eventContextMenu.y,
            zIndex: 10000,
            background: 'white',
            border: '1px solid var(--color-border-strong)',
            borderRadius: '4px',
            boxShadow: '0 4px 12px var(--color-shadow-20)',
            padding: '8px 0',
            minWidth: '220px',
            fontSize: '14px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="context-menu-item"
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              color: 'var(--color-primary)',
              fontWeight: 'bold'
            }}
            onClick={() => {
              const ev = eventContextMenu.event;
              const email = eventContextMenu.userEmail;
              setEventContextMenu({ visible: false, x: 0, y: 0, event: null, userEmail: null });
              setSelectedEventForConvert(ev);
              setSelectedUserForConvert(email);
              setShowEventConvertModal(true);
            }}
          >
            📌 {t('calendar.convertToTicket', 'In Ticket umwandeln')}
          </div>
          <div 
            className="context-menu-item"
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              color: 'var(--color-danger)',
              fontWeight: 'bold',
              borderTop: '1px solid var(--color-border-faint)'
            }}
            onClick={async () => {
              const ev = eventContextMenu.event;
              const email = eventContextMenu.userEmail;
              setEventContextMenu({ visible: false, x: 0, y: 0, event: null, userEmail: null });
              
              if (await confirm({ message: t('calendar.confirmDeleteEvent', 'Möchten Sie diesen Termin wirklich löschen?'), danger: true })) {
                try {
                  setOperationInProgress(true);
                  setOperationMessage(t('calendar.deletingEvent', 'Termin wird gelöscht...'));
                  
                  if (email && String(email).length > 0) {
                    await apiService.deleteCalendarEventForUser(email, ev.id);
                    await loadSpecificUsersData([email]);
                  } else {
                    await apiService.deleteCalendarEvent(ev.id);
                    await handleRefresh();
                  }
                } catch (err) {
                  console.error('Error deleting event:', err);
                  pushToast({ type: 'error', message: t('calendar.deleteError', 'Fehler beim Löschen des Termins.') });
                } finally {
                  setOperationInProgress(false);
                  setOperationMessage('');
                }
              }
            }}
          >
            🗑️ {t('calendar.deleteEvent', 'Eintrag löschen')}
          </div>
        </div>
      )}

      {showEventConvertModal && selectedEventForConvert && (
        <EventToTicketModal
          visible={showEventConvertModal}
          onClose={() => { 
            setShowEventConvertModal(false); 
            setSelectedEventForConvert(null);
            setSelectedUserForConvert(null);
          }}
          event={selectedEventForConvert}
          currentUser={currentUser}
          targetUserEmail={selectedUserForConvert || null}
          onSuccess={async () => {
            const u = selectedUserForConvert;
            try { if (u) { await loadSpecificUsersData([u]); } else { await handleRefresh(); } } catch (_) {}
          }}
        />
      )}

      {/* DROP CHOICE MODAL */}
      {dropChoiceModal.visible && dropChoiceModal.context && (
        <Modal open onClose={() => setDropChoiceModal({ visible: false, context: null })} title={t('calendar.dropChoice.title', 'Termin verschieben oder neuen erstellen?')} maxWidth="560px">
              <p style={{ marginBottom: 12 }}>{t('calendar.dropChoice.text', 'Sie haben einen bestehenden Termin bewegt. Wie möchten Sie fortfahren?')}</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={async () => {
                  const ctx = dropChoiceModal.context; if (!ctx) { setDropChoiceModal({ visible: false, context: null }); return; }
                  setDropChoiceModal({ visible: false, context: null });
                  // Verschieben wie bisher (inkl. cross-user)
                  try {
                    setOperationInProgress(true);
                    setOperationMessage(t('global.movingTicket', 'Ticket wird verschoben...'));
                    const ticket = ctx.ticket;
                    const originalStart = new Date(ticket.plannedStartDateTime);
                    const originalEnd = new Date(ticket.plannedEndDateTime);
                    const duration = originalEnd - originalStart;
                    const newStartDateTime = new Date(ctx.newStartDateTime);
                    const newEndDateTime = new Date(newStartDateTime.getTime() + duration);
                    const currentAssignedUser = ticket.assignedTo;
                    const isSameUser = currentAssignedUser === ctx.userEmail;
                if (isSameUser) {
                  await apiService.updateTicketPlanning(ticket.id, { startDateTime: newStartDateTime.toISOString(), endDateTime: newEndDateTime.toISOString(), isPlanned: true });
                } else {
                      try { await apiService.unplanTicket(ticket.id); } catch (_) {}
                      await apiService.planTicketForUser(ticket.id, { startDateTime: newStartDateTime.toISOString(), endDateTime: newEndDateTime.toISOString(), assignedTo: ctx.userEmail }, ctx.userEmail);
                    }
                    const affected = [ctx.userEmail]; if (currentAssignedUser && currentAssignedUser !== ctx.userEmail) affected.push(currentAssignedUser);
                    await loadSpecificUsersData(affected);
                // Wenn das Ticket aus der Sidebar gezogen wurde (überfällig), dort sofort entfernen
                try {
                  if (ctx && ctx.origin === 'sidebar' && removeTicketFromSidebar.current?.removeTicket) {
                    removeTicketFromSidebar.current.removeTicket(ticket.id);
                    console.log('🌍 [GLOBAL CALENDAR] Removed moved planned ticket from sidebar via modal (origin sidebar)');
                  }
                } catch (_) {}
                  } catch (err) {
                    console.error('Global dropChoice move error:', err);
                    setError(t('calendar.updateError', 'Fehler beim Aktualisieren.'));
                  } finally {
                    setOperationInProgress(false);
                    setOperationMessage('');
                  }
                }}>{t('calendar.dropChoice.move', 'Verschieben')}</button>
                <button className="btn btn-primary" onClick={async () => {
                  const ctx = dropChoiceModal.context; if (!ctx) { setDropChoiceModal({ visible: false, context: null }); return; }
                  setDropChoiceModal({ visible: false, context: null });
                  try {
                    setOperationInProgress(true);
                    setOperationMessage(t('global.creating', 'Erstelle...'));
                    const srcTicket = ctx.ticket;
                    // Prüfen ob schon zu Projekt/Subprojekt gehörig
                    let projectId = null;
                    if ((srcTicket.projectId && Number(srcTicket.projectId) > 0) || (srcTicket.subprojectId && Number(srcTicket.subprojectId) > 0)) {
                      projectId = Number(srcTicket.projectId) || null;
                    } else {
                      // Projekt ohne Untervorgänge erstellen
                      const projectName = srcTicket.title || 'Projekt';
                      // #158/#230 (L3): Kunde des Quelltickets auf den Auto-Vorgang übernehmen (wie CalendarView)
                      const projectRes = await apiService.createProject({ name: projectName, description: '', responsibleEmail: null, status: 'wartend', turnusCount: null, turnusEvery: null, turnusUnit: null, subprojects: [], isAutoCreated: true, customerId: srcTicket.customerId || null });
                      projectId = projectRes?.project?.id; if (!projectId) throw new Error('Projekt konnte nicht erstellt werden');
                    }
                    // Original als Block 1 (Block-Suffix prüfen)
                    const parsed = (() => {
                      try {
                        const raw = String(srcTicket.title || '');
                        const m = raw.match(/\s*\((\d+)\)\s*$/);
                        if (m) {
                          const num = parseInt(m[1], 10);
                          const base = raw.replace(/\s*\(\d+\)\s*$/, '').trim();
                          return { baseTitle: base, existingBlock: (Number.isNaN(num) ? null : num) };
                        }
                        return { baseTitle: raw.trim(), existingBlock: null };
                      } catch(_) { return { baseTitle: String(srcTicket.title || '').trim(), existingBlock: null }; }
                    })();
                    const shouldRenameOriginal = (parsed.existingBlock == null);
                    const block1Title = shouldRenameOriginal ? `${parsed.baseTitle} (1)` : srcTicket.title;
                    await apiService.updateTicket(srcTicket.id, { title: block1Title, projectId, subprojectId: null });
                    // neuen Block mit nächster Nummer anlegen
                    const nextBlockNum = (parsed.existingBlock ? parsed.existingBlock + 1 : 2);
                    const block2Title = `${parsed.baseTitle} (${nextBlockNum})`;
                    const assignedTo = ctx.userEmail || srcTicket.assignedTo || '';
                    // #158/#230 (L3): Kunde + Ansprechpartner explizit vererben (Server validiert den Kontakt)
                    const created2 = await apiService.createTicket({ title: block2Title, description: srcTicket.description || '', priority: srcTicket.priority || 'normal', assignedTo: assignedTo, category: srcTicket.category || 'Projekt', tags: srcTicket.tags || [], calenderMarker: srcTicket.calenderMarker || null, projectId, subprojectId: null, customerId: srcTicket.customerId || null, contactId: srcTicket.contactId || null });
                    const block2Ticket = created2.ticket || created2;
                    // Termin am Ziel
                    const originalStart = new Date(srcTicket.plannedStartDateTime);
                    const originalEnd = new Date(srcTicket.plannedEndDateTime);
                    const duration = originalEnd - originalStart;
                    const newStartDateTime = new Date(ctx.newStartDateTime);
                    const newEndDateTime = new Date(newStartDateTime.getTime() + duration);
                    await apiService.planTicketForUser(block2Ticket.id, { startDateTime: newStartDateTime.toISOString(), endDateTime: newEndDateTime.toISOString(), assignedTo: ctx.userEmail }, ctx.userEmail);
                    await loadSpecificUsersData([ctx.userEmail]);
                    try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'drop_choice_duplicate_global' } })); } catch (_) {}
                  } catch (err) {
                    console.error('Global dropChoice duplicate error:', err);
                    setError(t('calendar.createError', 'Fehler beim Erstellen.'));
                  } finally {
                    setOperationInProgress(false);
                    setOperationMessage('');
                  }
                }}>{t('calendar.dropChoice.createAnother', 'Weiteren erstellen')}</button>
              </div>
        </Modal>
      )}

      {/* CREATE PROJECT WIZARD entfernt (Kontextmenü-Feature entfernt) */}

      <style jsx>{`
        .global-calendar-with-sidebar {
          display: flex;
          height: 100vh;
          gap: 20px;
        }

        .global-calendar-main {
          flex: 1;
          overflow: hidden;
        }

        .global-calendar-view {
          padding: 20px 0;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        :root {
          --time-col-width: 80px;
        }

        .global-calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          flex-shrink: 0;
          max-height: 40px;
        }

        .global-calendar-header h2 {
          margin: 0;
          color: var(--color-text);
          display: flex;
          align-items: center;
        }

        .global-calendar-actions {
          display: flex;
          gap: 10px;
        }

        .global-calendar-empty {
          text-align: center;
          padding: 60px 20px;
          color: var(--color-neutral);
        }

        .global-calendar-empty p {
          margin-top: 20px;
          font-size: 18px;
        }

        .global-calendar-grid {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 10px var(--color-shadow-10);
 
          display: flex;
          flex-direction: column;
        }

        .global-calendar-week-header {
          background: var(--color-bg);
          border-bottom: 2px solid var(--color-border);
        }

        /* Grouped user header and day header styling */
        .global-calendar-user-header {
          background: var(--color-surface);
          border-bottom: 1px solid var(--color-border);
        }

        .user-group-header {
          padding: 12px 10px;
          font-weight: bold;
          text-align: center;
          border-right: 1px solid var(--color-border);
        }

        .global-calendar-day-header {
          background: var(--color-bg);
          border-bottom: 2px solid var(--color-border);
        }

        /* Ensure header cells do not stretch grid columns */
        .day-header {
          min-width: 0;
        }

        .user-group-header {
          min-width: 0;
        }

        .group-even {
          background-color: var(--color-surface);
        }

        .group-odd {
          background-color: var(--color-surface-zebra); /* subtle blue-ish */
        }

        /* Visual separation for user groups */
        .user-group-start {
          border-left: 3px solid var(--color-border-dark) !important; /* dark gray */
        }

        .user-group-end {
          box-shadow: inset -3px 0 0 0 var(--color-border-dark); /* dark gray */
        }

        .time-column-header {
          padding: 15px 10px;
          font-weight: bold;
          text-align: center;
          border-right: 1px solid var(--color-border);
          background: var(--color-surface);
        }

        .time-column-header {
          padding: 15px 10px;
          font-weight: bold;
          text-align: center;
          border-right: 1px solid var(--color-border);
        }

        .user-header {
          padding: 15px 10px;
          background: var(--color-bg);
          border-right: 1px solid var(--color-border);
          text-align: center;
          min-width: 0;
        }

        .user-name {
          font-weight: bold;
          font-size: 14px;
          color: var(--color-text);
          margin-bottom: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .time-column {
          border-right: 1px solid var(--color-border);
          height: calc(100vh - 300px);
          min-height: 400px;
          width: var(--time-col-width);
        }

        .time-slot {
          align-items: center;
          border-bottom: 1px solid var(--color-border);
          color: var(--color-neutral);
          display: flex;
          font-size: 12px;
          height: 10%;
          justify-content: center;
          padding: 8px 10px;
          text-align: center;
          box-sizing: border-box;
        }

        .user-column {
          border-right: 1px solid var(--color-border);
          position: relative;
          transition: background-color 0.2s ease;
        }

        .user-column.group-even {
          background: var(--color-surface);
        }

        .user-column.group-odd {
          background: var(--color-surface-zebra);
        }

        .user-column.drop-zone.drag-over {
          background-color: var(--color-primary-tint-10);
          border-right: 2px solid var(--color-primary-legacy);
        }

        .user-day-section {
          position: relative;
          height: calc(100vh - 300px);
          min-height: 400px;
        }

        .user-day-events {
          position: relative;
          height: 100%;
          z-index: 2;
        }

        .time-slot-bg {
          height: 10%;
          border-bottom: 1px solid var(--color-border-faint-alt);
        }

        .global-calendar-event {
          z-index: 10;
          transition: all 0.2s ease;
        }

        .global-calendar-ticket {
          z-index: 10;
          cursor: pointer;
          transition: all 0.2s ease, opacity 0.2s ease, transform 0.2s ease;
        }

        .global-calendar-ticket:hover {
          transform: scale(1.02);
          box-shadow: 0 4px 12px var(--color-shadow-20);
        }

        .global-calendar-ticket.dragging {
          opacity: 0.5;
          transform: scale(0.95);
          box-shadow: 0 8px 16px var(--color-shadow-30);
          z-index: 1000;
          transition: all 0.1s ease;
        }

        /* Positioned items styling */
        .global-calendar-event-positioned {
          z-index: 10;
        }

        .global-calendar-ticket-positioned {
          z-index: 10;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .global-calendar-ticket-positioned.operation-blocked {
          cursor: not-allowed;
          opacity: 0.7;
          filter: grayscale(20%);
        }

        .global-calendar-ticket-positioned:hover {
          transform: scale(1.02);
          box-shadow: 0 4px 12px var(--color-shadow-20);
        }

        .global-calendar-ticket-positioned.dragging {
          opacity: 0.5;
          transform: scale(0.95);
          box-shadow: 0 8px 16px var(--color-shadow-30);
          z-index: 1000;
          transition: all 0.1s ease;
        }

        /* Drag hover effects */
        .user-column.drop-zone {
          position: relative;
          transition: all 0.3s ease;
        }

        .user-column.drop-zone.operation-blocked {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .user-column.drop-zone.drag-over {
          background-color: var(--color-primary-tint-10);
          border-right: 3px solid var(--color-primary-legacy);
          transform: scale(1.01);
        }

        /* Entfernte pseudo Overlays während Drag-Over (Icon/Animation) */

        @keyframes slide {
          0% { background-position: 0 0; }
          100% { background-position: 20px 20px; }
        }

        @keyframes bounce {
          0% { transform: translate(-50%, -50%) scale(1); }
          100% { transform: translate(-50%, -50%) scale(1.1); }
        }

        /* Resize handle improvements */
        .resize-handle {
          transition: all 0.2s ease;
        }

        .resize-handle:hover {
          background-color: var(--color-glass-90) !important;
        }

        /* Loading and error states */
        .loading, .error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: var(--color-neutral);
        }

        .loading p, .error p {
          margin-top: 20px;
          font-size: 18px;
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GlobalCalendarView; 